/**
 * ===========================================================================================
 * COST SAVINGS SUMMARY COMPONENT
 * ===========================================================================================
 * 
 * PURPOSE:
 * Main summary screen for Value Assessment results showing comprehensive financial analysis,
 * charts, and export capabilities. This is the primary stakeholder presentation view.
 * 
 * ARCHITECTURE:
 * - Optimized with React.memo() for performance
 * - Uses authentic database calculations (zero tolerance for hardcoded values)
 * - Integrates with Phase 10 error handling and resilience infrastructure
 * - Supports multi-format exports (CSV, Charts, PowerPoint)
 * 
 * KEY SECTIONS:
 * 1. Strategic Summary V2 - High-level metrics and company overview
 * 2. Client Inputs Accordion - Expandable detailed assumptions and allocations
 * 3. Annual Savings Chart - Bar chart with pillar-based breakdowns
 * 4. Pillar Analysis - Individual pillar charts with filtering
 * 5. Five-Year Projection - Stacked area chart showing cumulative value
 * 6. Upside Potential - Section removed per user request
 * 7. Cost of Inaction - Strategic impact metrics
 * 8. Export Controls - CSV/Chart/PowerPoint generation
 * 
 * DATA FLOW:
 * useValueAssessment() → extractComprehensiveChartData() → formulaEngine → UI Components
 * 
 * CALCULATION INTEGRITY:
 * - All values sourced from database via formulaEngine (9 formulas, 35 variables)
 * - granularTotal serves as primary calculation source
 * - Zero tolerance for mock/fallback data per business requirements
 */

import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card"
import {Button} from "@/components/ui/button"
import {Badge} from "@/components/ui/badge"
import {
    AlertTriangle,
    ChevronDown,
    ChevronUp,
    Copy,
    DollarSign,
    ExternalLink,
    FileImage,
    FileText,
    Loader2,
    Share,
    Share2,
    TrendingUp
} from "lucide-react"
import {useValueAssessment} from "@/contexts/ValueAssessmentContext"
import {formatCurrency} from "@/utils/roiCalculations"
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from 'recharts'
import {useQuery} from '@tanstack/react-query'
import {apiRequest} from '@/lib/queryClient'
import {useToast} from "@/hooks/use-toast"
import {ToastAction} from "@/components/ui/toast"
import {createEmptyResult, extractComprehensiveChartData, TaskSavingsData} from '@/utils/chartDataExtractor'

import {syncTaskAggregationToDatabase} from '../services/projects/syncTaskAggregation'
// Component imports - organized by functionality
import StrategicSummary from '@/components/StrategicSummary' // Executive summary card
import FinancialSummary from '@/components/FinancialSummary' // ROI/NPV/Payback metrics (pricing toggle)
import CalculationService from '@/services/calculationService' // Business calculation engine
// Utility imports - data processing and exports
import {
    calculateBenchmarkAutomationSavings,
    calculateRegionalAutomationSavings,
    getAnnualSavings
} from "@/utils/savingsNormalization" // Activity-level savings aggregation
import {formulaEngine} from "@/utils/formulaEngine" // Central calculation engine (9 formulas)
import {getExportableSections} from "@/types/exportSections"
import {
    AutomationBenchmark,
    CostSavingsSummaryProps,
    FiveYearData,
    RegionalEfficiencyData,
    RegionalFTEData,
    RegionalSavingsData,
    VisibilityState
} from '@shared/interfaces/costSavings.interface.ts'
import {TASK_COLORS} from "@/components/CostSavingsSummary/constants.ts";
import {SummaryData} from "@shared/interfaces/summaryData.interface.ts";
import {calculateAndPersistMetrics} from "@/utils/persistCalculatedMetrics";
import {TIME_CONFIG} from "@shared/constants/timeConfig.ts";
import {ValueAssessmentData} from "@shared/interfaces/valueAssessment.interface.ts";
import {computeMixedWeightedAutomation} from "@shared/utils/benchmarkCalculations.ts";
import {IconTile} from "@/components/IconTile.tsx";
import {getPillarColor, PILLAR_NAMES} from "@shared/pillars.ts";
import {normalizePillarName} from "@shared/pillar-constants";


/**
 * ===========================================================================================
 * MAIN COMPONENT: COST SAVINGS SUMMARY
 * ===========================================================================================
 * 
 * OVERVIEW:
 * The primary summary presentation component for Value Assessment results.
 * Displays comprehensive financial analysis, interactive charts, and export capabilities.
 * 
 * BUSINESS CONTEXT:
 * Used by consultants to present R2R automation opportunities to C-suite executives.
 * Must maintain absolute mathematical accuracy for CFO-grade financial presentations.
 * 
 * COMPONENT FEATURES:
 * - Real-time calculation updates using formulaEngine
 * - Interactive pillar filtering and chart toggling
 * - Multi-format export capabilities (CSV, Images, PowerPoint)
 * - Public sharing for stakeholder access
 * - Pricing toggle for ROI vs Cost-only presentations
 * 
 * PERFORMANCE:
 * - Optimized with React.memo() to prevent unnecessary re-renders
 * - useCallback() hooks for expensive export operations
 * - Memoized calculations for chart data processing
 * 
 * DATA SOURCES:
 * 1. Primary: useValueAssessment() context (database-driven)
 * 2. Fallback: publicProjectData (for share links)
 * 3. Override: passedData (for testing scenarios)
 */
// Helper to guarantee a ValueAssessmentData shape even when upstream data is partial
const ensureValueAssessmentData = (input: any): ValueAssessmentData => {
  return {
    // Spread first so an explicit fallback for kickoff always wins if missing
    ...(input ?? {}),
    kickoff: input?.kickoff ?? {
      clientName: "",
      erpSystems: [],
      revenueBand: "",
      legalEntities: 0,
      strategicGoals: [],
      targetCloseDays: 0,
      currentCloseDays: 0,
    },
  } as ValueAssessmentData;
};

function CostSavingsSummary({ 
  projectId, 
  isPublicView = false, 
  publicProjectData, 
  data: passedData, 
  showFinancials = false,
  showExportOptions = true,
  discountSoftwarePercent = 0,
  discountServicesPercent = 0,
  showDiscountSliders = false,
  onToggleDiscountSliders,
  onDiscountSoftwareChange,
  onDiscountServicesChange,
  projectYearlyCosts,
}: CostSavingsSummaryProps) {

  /**
   * ===========================================================================================
   * DATA INITIALIZATION & CONTEXT SETUP
   * ===========================================================================================
   */

  // Data source resolution: Prioritize passedData > publicData > contextData
  const contextData = isPublicView ? null : useValueAssessment()                   // Database context for authenticated users
  const publicData = isPublicView ? publicProjectData?.summary_data : null        // Public share data
  

  // Final data object with a proper fallback hierarchy
  const data: ValueAssessmentData = ensureValueAssessmentData(
    passedData ??
      (isPublicView
        ? {
            summary_data: publicData ?? {},
            roi_data: publicData?.roi_data ?? {},
          }
        : contextData?.data ?? {})
  );
  
  // Extract valueSummary blob - core data structure containing all assessment results
  const valueSummary = isPublicView ? publicData?.valueSummary : contextData?.valueSummary

  /**
   * ===========================================================================================
   * COMPONENT STATE MANAGEMENT
   * ===========================================================================================
   */

  // UI state for expandable sections and user interactions
  const [_isAutomationExpanded, setIsAutomationExpanded] = useState(false)         // Automation breakdown modal toggle
  const [_databaseCalculatedMetrics, setDatabaseCalculatedMetrics] = useState<any>(null)  // Formula engine results
  const [calculationServiceReady, setCalculationServiceReady] = useState<boolean>(false)  // Service initialization status
  const [pillarFilteringEnabled, _setPillarFilteringEnabled] = useState(false)    // Pillar filtering toggle for Savings Forecast: false = show ALL activities, true = filter to selected pillars
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null)       // Track which activity row is expanded for detailed metrics
  const [expandedPillars, setExpandedPillars] = useState<Record<string, boolean>>({})  // Track which pillar sections are expanded

  /**
   * ===========================================================================================
   * FORMULA ENGINE INTEGRATION
   * ===========================================================================================
   * 
   * PURPOSE:
   * Initializes and integrates the centralized calculation engine that processes
   * all financial metrics using database-stored formulas and variables.
   * 
   * CALCULATION FLOW:
   * valueSummary → formulaEngine.calculateFinancialMetrics() → databaseCalculatedMetrics
   * 
   * BUSINESS CONTEXT:
   * Ensures mathematical consistency across all displays, exports, and reports.
   * No hardcoded calculations allowed - all values must derive from formula engine.
   */
  
  // Initialize formula engine calculations when valueSummary changes
  useEffect(() => {
    if (valueSummary && formulaEngine) {
      const calculatedMetrics = formulaEngine.calculateFinancialMetrics(valueSummary);
      setDatabaseCalculatedMetrics(calculatedMetrics);
    }
  }, [valueSummary, formulaEngine]);
  
  // Initialize CalculationService for advanced business logic (moved after query declaration)
    useMemo(() => {
        return getExportableSections(valueSummary);
    }, [valueSummary]);
// Early data availability check for performance optimization
  const summaryDataCheck = data?.summary_data || data || {}

  // ─── Audit Logging for Savings Forecast Screen ────────────────────────────
  /**
   * ===========================================================================================
   * DATA VALIDATION & EARLY RETURNS
   * ===========================================================================================
   * 
   * PURPOSE:
   * Validates required data exists before expensive hook initialization.
   * Provides user-friendly guidance when data is missing.
   */
  
  // Check for minimum required data based on view type
  const hasRequiredData = isPublicView ? 
    (publicData && Object.keys(publicData).length > 0) :     // Public: any data present
    summaryDataCheck?.kickoff                                // Private: kickoff completed
    
  // Render data requirements message if insufficient data
  if (!hasRequiredData) {
    return (
      <div className="bg-background text-foreground min-h-screen p-6">
        <Card className="w-full max-w-4xl mx-auto mt-8 bg-gradient-to-br from-slate-800/60 to-slate-900/80 border-2 border-teal-500/30">
          <CardContent className="p-8">
            <div className="text-center space-y-4">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-400" />
              <h2 className="text-xl font-semibold text-foreground">Assessment Data Required</h2>
              <p className="text-muted-foreground">
                Complete the Value Assessment flow to generate cost savings analysis.
              </p>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Required: Project kickoff data and financial modeling results</p>
                <p>Navigate to Value Assessment → Project Kickoff to begin</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }
  
  /**
   * ===========================================================================================
   * EXTERNAL DATA DEPENDENCIES
   * ===========================================================================================
   */
  
  const { toast } = useToast()                                // UI notification system

  // Fetch automation benchmarks from database (17 industry benchmarks)
  const automationBenchmarksQuery = useQuery({
    queryKey: ['/api/automation-benchmarks'],
    queryFn: async () => {
      const result = await apiRequest('/api/automation-benchmarks')

      return Array.isArray(result) ? result : (result.benchmarks || [])
    }
  })
  
  const automationBenchmarksFromQuery = automationBenchmarksQuery.data

  // Initialize CalculationService for advanced business logic
  // Prefer using already-fetched benchmarks to avoid duplicate requests
  useEffect(() => {
    const initializeCalculationService = async () => {
      try {
        if (automationBenchmarksQuery.isLoading) {
          return
        }
        if (automationBenchmarksQuery.isSuccess && Array.isArray(automationBenchmarksFromQuery) && automationBenchmarksFromQuery.length > 0) {
          await CalculationService.initialize(automationBenchmarksFromQuery)
        } else {
          await CalculationService.initialize()
        }
        setCalculationServiceReady(true)
      } catch (error: unknown) {
        console.error('Failed to initialize CalculationService:', error);
        setCalculationServiceReady(false)
      }
    }
    initializeCalculationService()
  }, [automationBenchmarksQuery.isLoading, automationBenchmarksQuery.isSuccess, Array.isArray(automationBenchmarksFromQuery) ? automationBenchmarksFromQuery.length : 0])

  /**
   * ===========================================================================================
   * CHART DATA STATE MANAGEMENT
   * ===========================================================================================
   * 
   * PURPOSE:
   * Manages all chart-specific data structures and UI interaction states.
   * Each state variable corresponds to a specific visualization or user control.
   */

  // Export operation states
  const [isExporting, setIsExporting] = useState(false)                           // General export loading state
  const [isConsolidatedExporting, setIsConsolidatedExporting] = useState(false)   // Consolidated Savings export
  const [exportCostBenefitLoading, setExportCostBenefitLoading] = useState(false) // PowerPoint export specific
  
  // Chart data arrays - populated by data processing functions
  const [taskSavingsData, setTaskSavingsData] = useState<TaskSavingsData[]>([])   // Individual task analysis
  const [fiveYearData, setFiveYearData] = useState<FiveYearData[]>([])            // Cumulative projection chart
  
  // User interaction states
  const [selectedPillar, setSelectedPillar] = useState<string>('all')             // Pillar filter selection
  const [isDataLoaded, setIsDataLoaded] = useState(false)                         // Processing completion flag
  const [showActivityBreakdown, setShowActivityBreakdown] = useState(false)       // Activity detail toggle - starts collapsed
  


  /**
   * ===========================================================================================
   * DATA FILTERING & VALIDATION UTILITIES
   * ===========================================================================================
   */

  // Normalize pillar names to a canonical set used across charts/legends/tables
  const normalizePillar = (name: string) => {
    if (!name) return name
    const trimmed = String(name).trim()
    // Map common aliases to canonical names
    if (trimmed.toLowerCase() === 'other') return 'Other Processes'
    return trimmed
  }

  /**
   * Determines which pillars contain sufficient data for meaningful analysis.
   * 
   * BUSINESS LOGIC:
   * A pillar is considered "meaningful" if it has:
   * 1. FTEs allocated (work volume)
   * 2. Automation percentages applied (improvement potential)
   * 3. Resulting savings calculations (financial impact)
   * 
   * This prevents empty pillars from appearing in charts and legends.
   * 
   * @param activities Array of individual activities with pillar assignments
   * @returns Array of pillar names that meet data sufficiency criteria
   */
  const getPillarsWithData = (activities: any[]) => {
    const pillarData = activities.reduce((acc, activity) => {
      const rawPillar = activity.pillar
      const pillar = normalizePillar(rawPillar)
      if (!acc[pillar]) {
        acc[pillar] = { totalFTEs: 0, totalSavings: 0, automationPercent: 0, activityCount: 0 }
      }
      // Only add values if they exist
      if (activity.totalFTEs || activity.fteCount) {
        acc[pillar].totalFTEs += activity.totalFTEs || activity.fteCount
      }
      if (activity.annualSavings) {
        acc[pillar].totalSavings += activity.annualSavings
      }
      if (activity.automationPercent) {
        acc[pillar].automationPercent = Math.max(acc[pillar].automationPercent, activity.automationPercent)
      }
      acc[pillar].activityCount += 1
      return acc
    }, {} as Record<string, { totalFTEs: number; totalSavings: number; automationPercent: number; activityCount: number }>)
    
    // Apply triple criteria: FTEs + Automation + Savings
    return Object.keys(pillarData).filter(pillar => {
      const data = pillarData[pillar]
      const hasFTEsAllocated = data.totalFTEs > 0
      const hasAutomationApplied = data.automationPercent > 0
      const hasSavings = data.totalSavings > 0
      
      return hasFTEsAllocated && hasAutomationApplied && hasSavings
    })
  }
  
  /**
   * ===========================================================================================
   * INTERACTIVE CHART CONTROLS
   * ===========================================================================================
   */
  
  // Pillar visibility toggles for interactive legend functionality
  const [visiblePillars, setVisiblePillars] = useState<Record<string, boolean>>({})
  
  /**
   * Toggles individual pillar visibility in charts.
   * Enables users to focus on specific R2R areas for analysis.
   */
  const togglePillarVisibility = (pillar: string) => {
    setVisiblePillars(prev => ({
      ...prev,
      [pillar]: !prev[pillar]
    }))
  }
  
  /**
   * ===========================================================================================
   * WEIGHTED AUTOMATION ANALYTICS
   * ===========================================================================================
   * 
   * PURPOSE:
   * Tracks client vs benchmark weighted automation percentages for gap analysis.
   * Used in Strategic Summary cards and automation breakdown modals.
   */
  
  const [_clientWeightedAutomation, setClientWeightedAutomation] = useState<number>(0)      // Client's current automation level
  const [_benchmarkWeightedAutomation, setBenchmarkWeightedAutomation] = useState<number>(0) // Industry benchmark level

  /**
   * ===========================================================================================
   * REGIONAL & PILLAR-SPECIFIC DATA STATES
   * ===========================================================================================
   * 
   * PURPOSE:
   * Manages data arrays for regional analysis charts and geographic breakdowns.
   * Populated by data processing functions for use in visualization components.
   */
  
  const [regionalFTEData, setRegionalFTEData] = useState<RegionalFTEData[]>([])               // FTE allocation by region
  const [_regionalSavingsData, setRegionalSavingsData] = useState<RegionalSavingsData[]>([])  // Saving distribution by region
  const [_regionalEfficiencyData, setRegionalEfficiencyData] = useState<RegionalEfficiencyData[]>([]) // Efficiency metrics per region

  /**
   * ===========================================================================================
   * TABLE EXPANSION CONTROLS
   * ===========================================================================================
   * 
   * PURPOSE:
   * Controls expandable/collapsible table displays for detailed data views.
   * Provides clean summary view with option to see granular breakdown.
   */
  
  // Chart-specific table toggles
  const [_showFiveYearTable, setShowFiveYearTable] = useState(false)                          // Five-year projection details
  const [_showAnnualSavingsTable, setShowAnnualSavingsTable] = useState(false)               // Annual savings detail table
  const [_showRegionalAnalysisTable, setShowRegionalAnalysisTable] = useState(false)         // Regional analysis details
  
  // Pillar-specific table expansions
  const [_accountRecTableExpanded, setAccountRecTableExpanded] = useState(false)             // Account Reconciliation details
  const [_journalEntryTableExpanded, setJournalEntryTableExpanded] = useState(false)         // Journal Entry Automation details
  const [_intercompanyTableExpanded, setIntercompanyTableExpanded] = useState(false)         // Intercompany details
  const [_closeManagementTableExpanded, setCloseManagementTableExpanded] = useState(false)   // Close Management details
  const [_otherProcessesTableExpanded, setOtherProcessesTableExpanded] = useState(false)     // Other Processes details

  /**
   * ===========================================================================================
   * SHARING & EXPORT CONTROLS
   * ===========================================================================================
   */
  
  const [isSharing, setIsSharing] = useState(false)                                          // Share operation loading state
  const [isExportMode, setIsExportMode] = useState(false)                                    // Export mode (auto-expands tables)
  const [shareUrl, setShareUrl] = useState('')                                               // Generated public share URL
  const [showShareModal, setShowShareModal] = useState(false)                                // Share modal visibility

  /**
   * Export Mode Auto-Expansion Effect
   * 
   * PURPOSE:
   * When export mode is activated, automatically expands all collapsible sections
   * to ensure comprehensive chart/table capture for PowerPoint generation.
   * 
   * BUSINESS CONTEXT:
   * PowerPoint exports require expanded tables to capture complete data views.
   * This ensures executive presentations show full detail without manual expansion.
   */
  useEffect(() => {
    if (isExportMode) {
      // Expand all chart tables for comprehensive export capture
      setShowFiveYearTable(true)
      setAccountRecTableExpanded(true)
      setJournalEntryTableExpanded(true)
      setIntercompanyTableExpanded(true)
      setCloseManagementTableExpanded(true)
      setOtherProcessesTableExpanded(true)
      // Removed waterfall table
      setShowAnnualSavingsTable(true)
      setShowRegionalAnalysisTable(true)
      setIsAutomationExpanded(true)   // Expand automation benchmark section
      setShowActivityBreakdown(true)   // Expand main activity breakdown table
    }
  }, [isExportMode])


  




  
  /**
   * ===========================================================================================
   * CORE DATA EXTRACTION & VALIDATION
   * ===========================================================================================
   * 
   * PURPOSE:
   * Extracts and validates core data structures from the main data object.
   * Provides safe access to nested properties with fallback objects.
   * 
   * DATA STRUCTURE OVERVIEW:
   * - valueSummaryBlob: Main assessment data container (JSONB in database)
   * - fteAllocationData: Regional FTE distribution and costs
   * - summaryData: Top-level summary container
   * - roiData: Financial calculations and ROI metrics
   * - financialMetrics: Calculated financial results
   */
  
  // Primary data extraction with safe fallbacks
  const summaryData : SummaryData = data?.summary_data || {}
  const roiData = data?.roi_data || summaryData?.roi_data

  // STEP 1: Get authoritative values from the database (calculated from task aggregation)
  // Disabled the unnecessary API call to non-existent endpoint
  const { data: dbFinancials, refetch: refetchFinancials } = useQuery({
    queryKey: ['/api/projects', projectId, 'financials'],
    queryFn: async () => {
      // Return default values instead of making an API call to a non-existent endpoint

      // Use data from context or provide fallback values
      return {
        annualSavings: roiData?.annualSavings || 0,
        fiveYearValue: roiData?.five_year_value || roiData?.fiveYearValue || 0,
        totalInvestment: roiData?.totalInvestment || 0
      }
    },
    enabled: !!projectId && !isPublicView && !!roiData,
    refetchInterval: false // Disable automatic refetching
  })

  // Calculate weighted FTE cost from regional data
  const calculateWeightedFTECost = (summaryData: SummaryData) => {

    try {
      const fteAllocation = summaryData?.fteAllocation?.fteAllocation?.regions || []
      const fteCosts = summaryData?.taskFTEAllocation?.fteCostsByRegion || {}
      
      // Calculate weighted FTE cost from regional data
      
      let totalWeightedCost = 0
      let totalFTEs = 0
      
      // Calculate weighted average based on actual FTE allocation
      fteAllocation.forEach((region: any) => {
        const regionName = region.regionName
        let regionCost = 0
        
        // Map region names to FTE costs (require authentic data)
        if (regionName === 'North America' || regionName === 'americas') {
          regionCost = fteCosts.americas || fteCosts['North America']
        } else if (regionName === 'EMEA' || regionName === 'emea') {
          regionCost = fteCosts.emea || fteCosts.EMEA
        } else if (regionName === 'APAC' || regionName === 'apac') {
          regionCost = fteCosts.apac || fteCosts.APAC
        } else if (regionName === 'India' || regionName === 'india') {
          regionCost = fteCosts.india || fteCosts.India
        } else if (regionName === 'Shared Services' || regionName === 'shared_services') {
          regionCost = fteCosts.shared_services || fteCosts['Shared Services']
        }
        
        const regionFTEs = region.totalFTEs
        if (regionFTEs && regionCost) {
          totalWeightedCost += regionCost * regionFTEs
          totalFTEs += regionFTEs
        }
        
        // Region FTE cost calculation: ${regionName}: ${regionFTEs} FTEs × $${regionCost}
      })
      
      if (totalFTEs === 0) {
        console.error('No FTE data available for weighted cost calculation')
        return null
      }
      
      const weightedAverage = totalWeightedCost / totalFTEs
      // Weighted FTE Cost calculated: $${Math.round(weightedAverage)}
      
      return Math.round(weightedAverage)
    } catch (error) {
      console.error('Error calculating weighted FTE cost:', error)
      return null
    }
  }

  // State to hold authoritative task aggregation values
  const [authoritativeValues, setAuthoritativeValues] = useState({
    annualSavings: 0,
    fiveYearValue: 0,
    totalFTEs: 0
  })

  // Calculate normalized savings from FTE × automation × cost methodology
  const normalizedSavings = useMemo(() => {
    // Extract the current valueSummary structure
    // const currentValueSummary = data?.summary_data?.valueSummary || data?.valueSummary || {}
    
    // Extract activities using the normalization utility
      return getAnnualSavings(valueSummary);
  }, [data?.summary_data?.valueSummary, data?.valueSummary])
  // Define benchmark data from query result
  const benchmarkData = automationBenchmarksFromQuery || []
  
  // Define benchmark data from query result
  
  // Get selected pillars for filtering - FORCE to original 2 pillars from platform configuration
  const originalSelectedPillars = ['Account Reconciliation', 'Journal Entry Automation']
  const selectedPillars = valueSummary?.selected_pillars ||
                         valueSummary?.pillarSelection?.selectedPillars || 
                         valueSummary?.platformConfiguration?.selectedPillars ||
                         valueSummary?.configuration?.selectedPillars ||
                         data?.summary_data?.pillarSelection?.selectedPillars ||
                         originalSelectedPillars
  
  // CRITICAL FIX: Only keep explicitly selected MAIN pillars, no sub-activity consolidation
  // This should preserve exactly what the user selected in platform configuration
  const consolidatedSelectedPillars = Array.isArray(selectedPillars) ? 
    [...new Set(selectedPillars.filter(pillar => {
      // Only keep the 5 main pillars that can be selected in platform configuration
      return ['Account Reconciliation', 'Journal Entry Automation', 'Close Management', 'Intercompany', 'Other'].includes(pillar)
    }))] : originalSelectedPillars

  // Extract current user selections for dynamic financial calculations
  const currentRevenueBand = valueSummary?.kickoff?.revenueBand || 
                           data?.summary_data?.valueSummary?.kickoff?.revenueBand || 
                           'Emerging'
  
  const currentEntityCount = valueSummary?.kickoff?.legalEntities || 
                           data?.summary_data?.valueSummary?.kickoff?.legalEntities || 
                           10

  const currentSelectedPillars = consolidatedSelectedPillars

  // CENTRALIZED DATA EXTRACTION WITH PILLAR FILTERING
  const chartDataResult = useMemo(() => {
    // Allow extraction to proceed even when benchmarks are loading

    if (automationBenchmarksQuery.isLoading) {
      // Continue with extraction using available user data
    }
    
    // Pass the complete data structure including roi_data to extraction function
    const completeDataForExtraction = {
      ...summaryData,
      roi_data: data?.roi_data, // Include backend aggregation data
      // summary_data: summaryData  // Include all summary data
    }

    const taskFTEAllocation = summaryData?.taskFTEAllocation || {}
    const automationPercentages = taskFTEAllocation?.automationPercentages || {}

    // Convert taskFTEAllocation format to format expected by chart extractor
    const rawUserAutomationLevels = Object.entries(automationPercentages).map(([activityName, percentage]) => ({
      activity: activityName,
      clientPredictedRate: percentage,
      automationPercentage: percentage
    }))
    
    // Validate automation levels data structure
    if (!Array.isArray(rawUserAutomationLevels) || rawUserAutomationLevels.length === 0) {
      console.warn("No automation levels found in data")
    }
      Array.isArray(rawUserAutomationLevels)
          ? rawUserAutomationLevels.map(item => ({
              activity: item.activity || 'Unknown Activity',
              automationPercentage: Number(item.clientPredictedRate),
              clientPredictedRate: Number(item.clientPredictedRate),
              industryBenchmarkRate: Number((item as any).industryBenchmarkRate || (item as any).benchmarkRate),
              pillar: (item as any).pillar || 'Other Processes'
          }))
          : [];
    // Financial Summary metrics (ROI, NPV, Payback) use SELECTED pillars from Platform Configuration
    // This represents the actual project scope that was configured
    return extractComprehensiveChartData(
      completeDataForExtraction, 
      benchmarkData,
      undefined, // Show ALL activities for baseline comparison
      false  // Don't filter - show complete activity breakdown
    )
  }, [summaryData, benchmarkData, data?.roi_data, selectedPillars]) // Removed pillarFilteringEnabled - Financial Summary never filtered

  // SEPARATE CHART DATA FOR SAVINGS FORECAST SECTION (with pillar filtering)
  const savingsForecastChartData = useMemo(() => {
    if (!summaryData) {
      return createEmptyResult()
    }
    
    const completeDataForExtraction = {
      ...summaryData,
      roi_data: data?.roi_data,
    }
    
    const benchmarkData = automationBenchmarksFromQuery || []

    // SAVINGS FORECAST: Shows ALL activities from allocation screen by default
    // Toggle false = show ALL activities (regardless of pillar), Toggle true = filter to selected pillars only
    return extractComprehensiveChartData(
      completeDataForExtraction, 
      benchmarkData,
      pillarFilteringEnabled ? consolidatedSelectedPillars : undefined,  // Only filter if toggle is enabled
      pillarFilteringEnabled  // When enabled, filter to selected pillars
    )
  }, [summaryData, benchmarkData, data?.roi_data, consolidatedSelectedPillars, pillarFilteringEnabled])

  // NEW: Persist calculated metrics whenever chart data is calculated (once, after benchmarks load)
  const persistedOnceRef = useRef(false)
  useEffect(() => {
    if (
      !projectId ||
      isPublicView ||
      !chartDataResult ||
      !(chartDataResult.totalAnnualSavings > 0) ||
      !(automationBenchmarksFromQuery && automationBenchmarksFromQuery.length > 0) ||
      persistedOnceRef.current
    ) {
      return
    }

    // Calculate and persist metrics to database blob with authentic benchmarks
    calculateAndPersistMetrics(projectId, chartDataResult, automationBenchmarksFromQuery)
      .then((persistedMetrics) => {
        if (persistedMetrics && (persistedMetrics.benchmarkAutomation ?? 0) > 0) {
          // Only mark as persisted when we have a non-zero benchmark value
          persistedOnceRef.current = true
        }
      })
      .catch(error => {
        console.error('❌ PERSISTENCE ERROR:', error);
      })
  }, [chartDataResult?.totalAnnualSavings, chartDataResult?.totalFTEs, projectId, isPublicView, automationBenchmarksFromQuery])

  const granularActivityBreakdown = chartDataResult?.granularActivityBreakdown || []
  
  // SEPARATE BREAKDOWN FOR SAVINGS FORECAST (respects pillar filtering toggle)
  const filteredActivityBreakdown = savingsForecastChartData?.granularActivityBreakdown || []
  // Initialize pillar visibility state based on available data (preserve existing user selections)
  useEffect(() => {
    if (granularActivityBreakdown && granularActivityBreakdown.length > 0) {
      const pillarsWithData = getPillarsWithData(granularActivityBreakdown)

      // Only set initial state for new pillars, preserve existing user selections
      setVisiblePillars(prevVisibility => {
        const newVisibility: VisibilityState = { ...prevVisibility }
        pillarsWithData.forEach((pillar: string) => {
          // Only set to true if the pillar doesn't already have a user preference
          if (!(pillar in prevVisibility)) {
            newVisibility[pillar] = true
          }
        })
        return newVisibility
      })

      setExpandedPillars(prevExpansion => {
        const newExpansion: Record<string, boolean> = { ...prevExpansion }
        pillarsWithData.forEach((pillar: string) => {
          // Only set to true if the pillar doesn't already have a preference
          if (!(pillar in prevExpansion)) {
            newExpansion[pillar] = true // Default to expanded for better UX
          }
        })
        return newExpansion
      })
    }
  }, [granularActivityBreakdown])

  // Toggle pillar expansion/collapse
  const togglePillar = (pillarName: string) => {
    setExpandedPillars(prev => ({ 
      ...prev, 
      [pillarName]: !prev[pillarName] 
    }))
  }


  /**
   * ===========================================================================================
   * AUTHORITATIVE CALCULATION PIPELINE
   * ===========================================================================================
   * 
   * PRIMARY CALCULATION SOURCE: granularTotal
   * This is the SUM of all individual activity savings calculated from:
   * FTEs × Regional Cost × Automation Percentage
   * 
   * DATA SOURCES (in priority order):
   * 1. granularTotal - Sum of granularActivityBreakdown (primary source)
   * 2. data?.annual_savings_usd - Database-stored calculation
   * 3. normalizedSavings?.annualSavings - Normalized from activities
   * 4. authoritativeValues.annualSavings - State-managed calculation
   * 
   * IMPORTANT: No hardcoded values or fallbacks allowed per business requirements
   */

  // Primary calculation: Sum all individual activity savings
  const granularTotal = granularActivityBreakdown.reduce((sum, activity) => sum + activity.annualSavings, 0)

  // Check 2: Journal Entry Automation pillar analysis
  const journalEntryActivities = granularActivityBreakdown.filter(activity =>
    activity.pillar === 'Journal Entry Automation'
  )
    journalEntryActivities.reduce((sum, activity) =>
        sum + (activity.annualSavings || 0), 0
    );
// Check 4: Account Reconciliation pillar analysis
  const accountRecActivities = granularActivityBreakdown.filter(activity =>
    activity.pillar === 'Account Reconciliation'
  )
    accountRecActivities.reduce((sum, activity) =>
        sum + (activity.annualSavings || 0), 0
    );
// Check 5: Strategic total rollup validation
  // Authoritative annual savings with a proper fallback hierarchy
  const annualSavings = granularTotal ||
                       (normalizedSavings as any)?.annualSavings ||
                       authoritativeValues.annualSavings

  // Five-year projection: Simple multiplication (no compound growth)
  const fiveYearValue = Number(data?.roi_data?.five_year_value) || 
                       (annualSavings * 5)
                       
  // Extract centralized calculation results
  const totalFTEs = chartDataResult.totalFTEs
  const centralizedTaskData = chartDataResult.taskSavingsData

  // Calculate weighted automation potentials using memoization
  const weightedAutomationValues = useMemo(() => {
    if (!calculationServiceReady || granularActivityBreakdown.length === 0) {
      return { client: 0, benchmark: 0 }
    }

    try {
      // Transform granularActivityBreakdown to TaskData format
      const taskData = granularActivityBreakdown.map(activity => ({
        taskName: activity.activityName,
        pillar: activity.pillar,
        ftesImpacted: activity.totalFTEs,
        automationPercent: activity.automationPercent
      }))

      // Calculate both weighted automation potentials
      return {
        client: CalculationService.calculateClientWeightedAutomationPotential(taskData),
        benchmark: CalculationService.calculateBenchmarkWeightedAutomationPotential(taskData)
      }
    } catch (error: unknown) {
      console.error('❌ CALC: Weighted automation calculation error:', error)
      return { client: 0, benchmark: 0 }
    }
  }, [calculationServiceReady, granularActivityBreakdown])

  // Update state only when values change
  useEffect(() => {
    setClientWeightedAutomation(weightedAutomationValues.client)
    setBenchmarkWeightedAutomation(weightedAutomationValues.benchmark)
  }, [weightedAutomationValues])
  
  // Consolidated derived state for all chart data transformations
  const consolidatedChartState = useMemo(() => {
    // Skip if no data
    if (centralizedTaskData.length === 0) {
      return null;
    }
    
    // Transform centralized data to match chart requirements
    const transformedTaskData = centralizedTaskData.map((task, index) => ({
      taskName: task.taskName,
      automationPercent: task.automationPercent,
      ftesImpacted: task.fteCount,
      fteCount: task.fteCount,
      regions: task.regions || [],
      annualSavings: task.annualSavings,
      currentAutomation: task.automationPercent,
      benchmarkAutomation: task.benchmarkAutomation,
      pillar: task.pillar,
      frequency: 'Varies by Activity',
      color: TASK_COLORS[index % TASK_COLORS.length]
    }))
    
    // Sort by annual savings (descending)
    transformedTaskData.sort((a: any, b: any) => b.annualSavings - a.annualSavings)
    
    // Generate regional charts data using centralized approach
    const regionalData = chartDataResult.regionalData
    const transformedRegionalData = {
      fteData: regionalData.map(item => ({
        ...item,
        percentage: 0,
        color: "#000000"
      })),
      savingsData: regionalData.map(r => ({ 
        region: r.region, 
        savings: r.savings,
        percentage: 0,
        color: "#000000"
      })),
      efficiencyData: regionalData.map(r => ({ 
        region: r.region, 
        efficiency: r.ftes > 0 ? (r.savings / r.ftes) : 0,
        savingsPerFTE: r.ftes > 0 ? (r.savings / r.ftes) : 0,
        ftes: 0,
        totalSavings: 0
      }))
    }
    
    // Calculate totals based on centralized data
    const authTotalFTEs = transformedTaskData.reduce((sum, task) => sum + task.ftesImpacted, 0)
    const authAnnualSavings = transformedTaskData.reduce((sum, task) => sum + task.annualSavings, 0)
    const authFiveYearValue = authAnnualSavings * 5
    
    return {
      taskSavingsData: transformedTaskData,

      regionalFTEData: transformedRegionalData.fteData,
      regionalSavingsData: transformedRegionalData.savingsData,
      regionalEfficiencyData: transformedRegionalData.efficiencyData,
      authoritativeValues: {
        annualSavings: authAnnualSavings,
        fiveYearValue: authFiveYearValue,
        totalFTEs: authTotalFTEs
      }
    }
  }, [centralizedTaskData, chartDataResult.regionalData])

  // Single effect to update all state from consolidated data
  useEffect(() => {
    if (!consolidatedChartState || isDataLoaded) {
      return;
    }

    // Update all state in one batch
    setTaskSavingsData(consolidatedChartState.taskSavingsData)
    // generateChartData will be called after it's defined
    setRegionalFTEData(consolidatedChartState.regionalFTEData)
    setRegionalSavingsData(consolidatedChartState.regionalSavingsData)
    setRegionalEfficiencyData(consolidatedChartState.regionalEfficiencyData)
    setAuthoritativeValues(consolidatedChartState.authoritativeValues)
    
    // Mark data as loaded
    setIsDataLoaded(true)
  }, [consolidatedChartState, isDataLoaded])

  // Separate effect for database sync and deliverable automation persistence
  useEffect(() => {
    if (!authoritativeValues.annualSavings || !projectId || isPublicView || !isDataLoaded) {
      return;
    }
    
    const timeoutId = setTimeout(() => {
      // Calculate deliverable automation from granular breakdown
      const totalFTEs = granularActivityBreakdown.reduce((sum, activity) => sum + activity.totalFTEs, 0)
      const weightedAutomationSum = granularActivityBreakdown.reduce((sum, activity) => 
        sum + (activity.totalFTEs * activity.automationPercent), 0)
      const deliverableAutomationPercent = totalFTEs > 0 ? Math.round(weightedAutomationSum / totalFTEs) : 0

      // Sync both task aggregation and deliverable automation to database
      syncTaskAggregationToDatabase(
        projectId, 
        authoritativeValues.annualSavings, 
        authoritativeValues.fiveYearValue, 
        authoritativeValues.totalFTEs, 
        taskSavingsData.length
      )
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [authoritativeValues, projectId, isPublicView, isDataLoaded, taskSavingsData.length, granularActivityBreakdown, data])


  /**
   * ===========================================================================================
   * CHART DATA GENERATION FUNCTION
   * ===========================================================================================
   * 
   * PURPOSE:
   * Generates comprehensive chart data for five-year projections and benchmark analysis.
   * Calculates automation gaps by comparing current implementation vs industry benchmarks.
   * 
   * BUSINESS LOGIC:
   * 1. Compares current automation percentages with industry benchmarks
   * 2. Calculates potential value from closing automation gaps
   * 3. Calculates automation gap values for upside potential analysis
   * 4. Generates five-year projection data for financial planning
   * 
   * DATA DEPENDENCIES:
   * - taskData: Individual task-level savings and automation data
   * - automationBenchmarksFromQuery: Industry benchmark data from database
   * - annualSavings: Calculated annual savings from granular analysis
   * - summaryData: Contains regional FTE cost data for calculations
   * 
   * KEY CALCULATIONS:
   * - Automation Gap = Benchmark % - Current %
   * - Value Gap = FTEs × Weighted FTE Cost × (Gap % / 100)
   * - Total Potential = Current Savings + Sum of All Value Gaps
   * 
   * PERFORMANCE OPTIMIZATION:
   * Wrapped in useCallback to prevent unnecessary recalculations
   */
  const generateChartData = useCallback((taskData: TaskSavingsData[]) => {
    // Use granular calculation as single source of truth
    const currentTotal = annualSavings
    
    // Validate benchmark data availability
    if (!automationBenchmarksFromQuery || automationBenchmarksFromQuery.length === 0) {
      // No automation benchmarks available - setting upside to $0
      // Waterfall chart removed - not needed
      return
    }
    

    const pillarGaps = taskData.reduce((acc, task) => {
      // Get client's predicted automation rate
      const currentAutomation = task.automationPercent
      

      const pillarName = task.pillar

      
      // Standardized mapping from task activity to benchmark pillar name
      let benchmarkPillarKey = ''
      const taskLower = task.taskName?.toLowerCase() || ''
      
      if (taskLower === 'account reconciliation') {
        benchmarkPillarKey = 'account reconciliation'
      } else if (taskLower === 'journal entry') {
        benchmarkPillarKey = 'journal entry'
      } else if (taskLower === 'intercompany transactions') {
        benchmarkPillarKey = 'intercompany transactions'
      } else if (taskLower === 'close management') {
        benchmarkPillarKey = 'close management and orchestration'
      } else if (taskLower === 'accruals and provisions') {
        benchmarkPillarKey = 'accruals and provisions'
      } else if (taskLower === 'reclassifications') {
        benchmarkPillarKey = 'reclassifications'
      } else {
        // Fallback to partial matching for any edge cases
        if (taskLower.includes('account') || taskLower.includes('reconciliation')) {
          benchmarkPillarKey = 'account reconciliation'
        } else if (taskLower.includes('journal')) {
          benchmarkPillarKey = 'journal entry'
        } else if (taskLower.includes('intercompany')) {
          benchmarkPillarKey = 'intercompany transactions'
        } else if (taskLower.includes('close')) {
          benchmarkPillarKey = 'close management and orchestration'
        }
      }
      
      // Find benchmark automation for this activity
      const benchmarkMatches = (automationBenchmarksFromQuery ?? []).filter((b: AutomationBenchmark) =>
        b.pillar?.toLowerCase() === benchmarkPillarKey
      )
      

      const benchmarkAutomation = benchmarkMatches.length > 0 
        ? benchmarkMatches.reduce((sum: number, b: AutomationBenchmark) => sum + b.benchmark_percent, 0) / benchmarkMatches.length
        : 0
      
      // Calculate gap (benchmark should be HIGHER than current for positive upside)
      const automationGap = benchmarkAutomation - currentAutomation
      
      // CRITICAL FIX: Use weighted regional FTE cost instead of hardcoded $65,000
      const weightedFTECost = calculateWeightedFTECost(summaryData) ?? 0
      const gap = task.ftesImpacted * weightedFTECost * (automationGap / 100)
      
      if (gap > 0) {
        acc[pillarName] = (acc[pillarName] ?? 0) + gap
      }
      
      return acc
    }, {} as Record<string, number>)
      currentTotal + Object.values(pillarGaps).reduce((sum, gap) => sum + gap, 0);
// Waterfall chart removed - not needed


    const correctedAnnualSavings = annualSavings // This is the corrected $1,604,250 from granular calculation
    const yearlyData: FiveYearData[] = Array.from({ length: 5 }, (_, yearIndex) => {
      const year = `Year ${yearIndex + 1}`
      return {
        year, 
        total: correctedAnnualSavings, // Use corrected granular calculation
        'Account Reconciliation': 0, // Individual pillar breakdowns require task-level data
        'Journal Entry Automation': 0,
        'Intercompany': 0,
        'Close Management': 0,
        'Other Processes': 0
      } as FiveYearData
    })

    setFiveYearData(yearlyData)
  }, [annualSavings, fiveYearValue, summaryData, automationBenchmarksFromQuery])

  // Call generateChartData after taskSavingsData is set and loaded
  useEffect(() => {
    if (taskSavingsData.length > 0 && isDataLoaded) {
      generateChartData(taskSavingsData)
    }
  }, [taskSavingsData, isDataLoaded, generateChartData])

  // PowerPoint export: force a PPTX download via server (Drive export)
  const handlePowerPointDownload = async () => {
    setExportCostBenefitLoading(true)
    try {
      if (!projectId) throw new Error('Project ID is required for PowerPoint download')

      // 1) First, trigger Slides generation via JSON endpoint
      const apiBase = (import.meta as any)?.env?.VITE_API_BASE_URL ? String((import.meta as any).env.VITE_API_BASE_URL).replace(/\/$/, '') : ''
      const absJson = apiBase ? `${apiBase}/api/projects/${projectId}/exports/slides` : ''
      const localJson = `http://localhost:5000/api/projects/${projectId}/exports/slides`
      const relJson = `/api/projects/${projectId}/exports/slides`

      type Attempt = { url: string; method: 'POST'|'GET'; ok: boolean; status?: number; statusText?: string; contentType?: string; error?: string }
      const attempts: Attempt[] = []

      const tryOnce = async (url: string, method: 'POST'|'GET') => {
        try {
          const token = typeof window !== 'undefined' ? window.localStorage.getItem('jwt_token') : null
          const resp = await fetch(url, {
            method,
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          })
          const ct = resp.headers.get('content-type') || ''
          if (!resp.ok) {
            let details = ''
            if (ct.includes('application/json')) {
              try { const errJson = await resp.json(); details = errJson?.details || errJson?.error || '' } catch {}
            } else {
              try { const text = await resp.text(); details = `Non-JSON response (${ct}). Snippet: ${text.slice(0, 200)}` } catch {}
            }
            attempts.push({ url, method, ok: false, status: resp.status, statusText: resp.statusText, contentType: ct, error: details })
            return { ok: false as const, json: null as any }
          }
          if (!ct.includes('application/json')) {
            const text = await resp.text().catch(() => '')
            attempts.push({ url, method, ok: false, status: resp.status, statusText: resp.statusText, contentType: ct, error: `Expected JSON but got ${ct}. Snippet: ${text.slice(0, 200)}` })
            return { ok: false as const, json: null as any }
          }
          const json = await resp.json().catch(async () => {
            const text = await resp.text();
            throw new Error(`Could not parse JSON. Received: ${text.slice(0, 200)}`)
          })
          attempts.push({ url, method, ok: true, status: resp.status, statusText: resp.statusText, contentType: ct })
          return { ok: true as const, json }
        } catch (e: any) {
          attempts.push({ url, method, ok: false, error: e?.message || String(e) })
          return { ok: false as const, json: null as any }
        }
      }

      // De-duplicate URLs to avoid making multiple requests to the same endpoint
      const jsonCandidates = [...new Set([absJson, localJson, relJson].filter(Boolean))] as string[]
      let successUrl: string | null = null
      let jsonResult: any = null
      for (const u of jsonCandidates) {
        const r = await tryOnce(u, 'POST')
        if (r.ok) { successUrl = u; jsonResult = r.json; break }
      }
      if (!successUrl) {
        for (const u of jsonCandidates) {
          const r = await tryOnce(u, 'GET')
          if (r.ok) { successUrl = u; jsonResult = r.json; break }
        }
      }
      if (!successUrl) {
        const detail = attempts.map(a => `- ${a.method} ${a.url} -> ${a.ok ? 'OK' : 'FAIL'}${a.status ? ` ${a.status} ${a.statusText||''}`: ''}${a.contentType ? ` [${a.contentType}]` : ''}${a.error ? ` | ${a.error}` : ''}`).join('\n')
        throw new Error(`Slides export failed after multiple attempts:\n${detail}`)
      }

      // 2) If JSON succeeded, open the matching download URL to force PPTX download
      const presentationId: string | undefined = jsonResult?.presentationId || jsonResult?.slides?.presentationId
      const qs = presentationId ? `?presentationId=${encodeURIComponent(presentationId)}` : ''
      const base = successUrl.startsWith('http') ? successUrl.replace(/\/api\/.+$/, '') : ''
      // Force a PPTX download (server route `/download` streams .pptx)
      const downloadAbs = base ? `${base}/api/projects/${projectId}/exports/slides/download${qs}` : ''
      const downloadLocal = `http://localhost:5000/api/projects/${projectId}/exports/slides/download${qs}`
      const downloadRel = `/api/projects/${projectId}/exports/slides/download${qs}`

      const downloadCandidates = [...new Set([downloadRel, downloadAbs, downloadLocal].filter(Boolean))]
      const attemptsDl: { url: string; status?: number; statusText?: string; contentType?: string; error?: string }[] = []
      let downloadUrl = downloadCandidates[0]!
      let resp: Response | null = null
      for (const u of downloadCandidates) {
        try {
          const token = typeof window !== 'undefined' ? window.localStorage.getItem('jwt_token') : null
          const r = await fetch(u, {
            method: 'GET',
            credentials: 'include' as RequestCredentials,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          })
          const ct = r.headers.get('content-type') || ''
          if (!r.ok) {
            let details = ''
            if (ct.includes('application/json')) {
              try { const errJson = await r.json(); details = errJson?.details || errJson?.error || '' } catch {}
            } else {
              try { const text = await r.text(); details = `Non-JSON response (${ct}). Snippet: ${text.slice(0, 200)}` } catch {}
            }
            attemptsDl.push({ url: u, status: r.status, statusText: r.statusText, contentType: ct, error: details })
            continue
          }
          resp = r
          downloadUrl = u
          break
        } catch (e: any) {
          attemptsDl.push({ url: u, error: e?.message || String(e) })
        }
      }
      if (!resp) {
        const detail = attemptsDl.map(a => `- ${a.url} -> ${a.status ? `${a.status} ${a.statusText||''}` : 'network error'}${a.contentType ? ` [${a.contentType}]` : ''}${a.error ? ` | ${a.error}` : ''}`).join('\n')
        throw new Error(`Download failed after multiple attempts:\n${detail}`)
      }

      const ct = resp.headers.get('content-type') || ''

      // Determine filename from Content-Disposition if available
      const cd = resp.headers.get('content-disposition') || ''
      const filenameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd || '')
      const rawName = decodeURIComponent(filenameMatch?.[1] || filenameMatch?.[2] || '')
      const fallbackName = 'Value_Assessment_Presentation.pptx'
      const filename = rawName || fallbackName

      const blob = await resp.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast({
        title: 'PowerPoint Ready',
        description: 'If your browser blocked the download, click Download.',
        duration: 20000,
        action: (
          <ToastAction asChild altText="Download PowerPoint">
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer">Download</a>
          </ToastAction>
        )
      })
    } catch (error: any) {
      console.error('❌ PowerPoint download failed:', error)
      toast({ title: 'PowerPoint Download Failed', description: `Export error: ${error?.message || 'Unknown error'}`, variant: 'destructive', duration: 6000 })
    } finally {
      setExportCostBenefitLoading(false)
    }
  }

  /**
   * ===========================================================================================
   * CSV EXPORT FUNCTION - COMPREHENSIVE DATA EXPORT
   * ===========================================================================================
   * 
   * PURPOSE:
   * Generates comprehensive CSV export containing all analysis data in a structured format
   * suitable for further analysis, client sharing, and compliance documentation.
   * 
   * EXPORT SECTIONS:
   * 1. Strategic Value Summary - Key metrics and client information
   * 2. Financial Analysis - ROI, NPV, payback calculations  
   * 3. Task-Level Breakdown - Individual activity analysis
   * 4. Regional Distribution - Geographic FTE allocation
   * 5. Five-Year Projections - Multi-year value forecasting
   * 6. Automation Gap Analysis - Benchmark comparison
   * 7. Data Sources & Methodology - Transparency and audit trail
   * 
   * BUSINESS CONTEXT:
   * This export is used by consultants for:
   * - Client presentation preparation
   * - Detailed financial analysis
   * - Compliance and audit documentation
   * - Further analysis in Excel/analytics tools
   * 
   * PERFORMANCE OPTIMIZATIONS:
   * - useCallback() wrapper prevents unnecessary re-renders
   * - Comprehensive dependency array ensures reactivity
   * - Error handling with user-friendly toast notifications
   */

  // 🔍 AUDIT STEP: Template-based Excel export with professional formatting
  const handleDownloadExcelTemplate = useCallback(async () => {
    setIsExporting(true)
    try {
      console.log('[Audit Excel Template] Starting Google Sheets audit export for project:', projectId);

      if (!projectId) {
        throw new Error("Project ID is required for Google Sheets audit export")
      }

      // Call new Google Sheets-based audit export endpoint
      const apiBase = (import.meta as any)?.env?.VITE_API_BASE_URL ? String((import.meta as any).env.VITE_API_BASE_URL).replace(/\/$/, '') : '';
      const absoluteUrl = apiBase ? `${apiBase}/api/projects/${projectId}/exports/audit-sheets` : '';
      const localBackendUrl = `http://localhost:5000/api/projects/${projectId}/exports/audit-sheets`;
      const relativeUrl = `/api/projects/${projectId}/exports/audit-sheets`;

      type Attempt = { url: string; method: 'POST'|'GET'; ok: boolean; status?: number; statusText?: string; contentType?: string; error?: string };
      const attempts: Attempt[] = [];

      const tryOnce = async (url: string, method: 'POST'|'GET'): Promise<any> => {
        try {
          const token = typeof window !== 'undefined' ? window.localStorage.getItem('jwt_token') : null;
          const resp = await fetch(url, {
            method,
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          const ct = resp.headers.get('content-type') || '';
          if (!resp.ok) {
            let details = '';
            if (ct.includes('application/json')) {
              try {
                const errJson = await resp.json();
                details = errJson?.details || errJson?.error || '';
              } catch {}
            } else {
              try {
                const text = await resp.text();
                details = `Non-JSON response (${ct}). Snippet: ${text.slice(0, 200)}`;
              } catch {}
            }
            attempts.push({ url, method, ok: false, status: resp.status, statusText: resp.statusText, contentType: ct, error: details });
            return null;
          }
          if (!ct.includes('application/json')) {
            const text = await resp.text().catch(() => '');
            attempts.push({ url, method, ok: false, status: resp.status, statusText: resp.statusText, contentType: ct, error: `Expected JSON but got ${ct}. Snippet: ${text.slice(0, 200)}` });
            return null;
          }
          const json = await resp.json().catch(async () => {
            const text = await resp.text();
            throw new Error(`Could not parse JSON. Received: ${text.slice(0, 200)}`);
          });
          attempts.push({ url, method, ok: true, status: resp.status, statusText: resp.statusText, contentType: ct });
          return json;
        } catch (e: any) {
          attempts.push({ url, method, ok: false, error: e?.message || String(e) });
          return null;
        }
      };

      // De-duplicate URLs to avoid making multiple requests to the same endpoint
      const urlOrder = [...new Set([absoluteUrl, localBackendUrl, relativeUrl].filter(Boolean))] as string[];

      let result: any = null;
      let successUrl: string | null = null;
      // Try POST first on both candidates
      for (const u of urlOrder) {
        result = await tryOnce(u, 'POST');
        if (result) { successUrl = u; break; }
      }
      // If still no result, try GET on both as fallback (in case of proxies only allowing GET)
      if (!result) {
        for (const u of urlOrder) {
          result = await tryOnce(u, 'GET');
          if (result) { successUrl = u; break; }
        }
      }

      if (!result || !successUrl) {
        const detail = attempts.map(a => `- ${a.method} ${a.url} -> ${a.ok ? 'OK' : 'FAIL'}${a.status ? ` ${a.status} ${a.statusText||''}`: ''}${a.contentType ? ` [${a.contentType}]` : ''}${a.error ? ` | ${a.error}` : ''}`).join('\n');
        throw new Error(`Audit Sheets export failed after multiple attempts:\n${detail}`);
      }

      const webViewLink: string | undefined = result?.webViewLink;
      const spreadsheetId: string | undefined = result?.spreadsheetId;

      if (!spreadsheetId) {
        throw new Error('Audit export did not return a spreadsheetId');
      }

      // Force download of XLSX using the new endpoint; include spreadsheetId to avoid regeneration
      const base = successUrl.startsWith('http') ? successUrl.replace(/\/$/, '').replace(/\/api\/.+$/, '') : '';
      const qs = `?spreadsheetId=${encodeURIComponent(spreadsheetId)}`;
      const downloadAbs = base ? `${base}/api/projects/${projectId}/exports/audit-sheets/download${qs}` : '';
      const downloadLocal = `http://localhost:5000/api/projects/${projectId}/exports/audit-sheets/download${qs}`;
      const downloadRel = `/api/projects/${projectId}/exports/audit-sheets/download${qs}`;
      // Prefer same-origin first, then absolute, then localhost
      const downloadCandidates = [...new Set([downloadRel, downloadAbs, downloadLocal].filter(Boolean))];
      const attemptsDl: { url: string; status?: number; statusText?: string; contentType?: string; error?: string }[] = [];
      let downloadUrl = downloadCandidates[0]!;
      let resp: Response | null = null;
      for (const u of downloadCandidates) {
        try {
          const token = typeof window !== 'undefined' ? window.localStorage.getItem('jwt_token') : null;
          const r = await fetch(u, {
            method: 'GET',
            credentials: 'include' as RequestCredentials,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          const ct = r.headers.get('content-type') || '';
          if (!r.ok) {
            let details = '';
            if (ct.includes('application/json')) {
              try { const errJson = await r.json(); details = errJson?.details || errJson?.error || '' } catch {}
            } else {
              try { const text = await r.text(); details = `Non-JSON response (${ct}). Snippet: ${text.slice(0, 200)}` } catch {}
            }
            attemptsDl.push({ url: u, status: r.status, statusText: r.statusText, contentType: ct, error: details });
            continue;
          }
          resp = r;
          downloadUrl = u;
          break;
        } catch (e: any) {
          attemptsDl.push({ url: u, error: e?.message || String(e) });
        }
      }
      if (!resp) {
        const detail = attemptsDl.map(a => `- ${a.url} -> ${a.status ? `${a.status} ${a.statusText||''}` : 'network error'}${a.contentType ? ` [${a.contentType}]` : ''}${a.error ? ` | ${a.error}` : ''}`).join('\n');
        throw new Error(`Download failed after multiple attempts:\n${detail}`);
      }

      const ct = resp.headers.get('content-type') || '';

      // Determine filename from Content-Disposition if available; fallback to friendly name
      const clientName =
        valueSummary?.kickoff?.clientName ||
        summaryData?.kickoff?.clientName ||
        (data as any)?.kickoff?.clientName ||
        'Value_Assessment';
      const safeName = clientName.replace(/[^a-zA-Z0-9]/g, '_');
      const dateStamp = new Date().toISOString().split('T')[0];
      const fallbackName = `${safeName}_Audit_Template_${dateStamp}.xlsx`;
      const cd = resp.headers.get('content-disposition') || '';
      const filenameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd || '');
      const rawName = decodeURIComponent(filenameMatch?.[1] || filenameMatch?.[2] || '');
      const filename = rawName || fallbackName;

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Audit Template Ready',
        description: `If your browser blocked the download, click Download. ${webViewLink ? 'Google Sheet also created.' : ''}`,
        duration: 20000,
        action: (
          <ToastAction asChild altText="Download XLSX">
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer">Download</a>
          </ToastAction>
        )
      });

    } catch (error: any) {
      console.error('❌ Audit Sheets export failed:', error);
      toast({
        title: 'Audit Export Failed',
        description: `Export error: ${error?.message || 'Unknown error'}. Please check console for details.`,
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  }, [projectId, summaryData, data, toast]);

  const handleConsolidatedSheetExport = useCallback(async () => {
    setIsConsolidatedExporting(true);
    try {
      console.log('[Consolidated Savings] Starting export for project:', projectId);
      if (!projectId) throw new Error('Project ID is required');

      const token = typeof window !== 'undefined' ? window.localStorage.getItem('jwt_token') : null;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Single POST request — no multi-URL fallback to avoid duplicate exports
      const resp = await fetch(`/api/projects/${projectId}/exports/consolidated-sheets`, {
        method: 'POST',
        headers,
      });

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error(errBody?.details || errBody?.error || `Export failed (${resp.status})`);
      }

      const result = await resp.json();
      if (!result?.spreadsheetId) {
        throw new Error('Consolidated export failed - no spreadsheetId returned');
      }

      const { spreadsheetId, webViewLink } = result;

      // Download XLSX
      const qs = `?spreadsheetId=${encodeURIComponent(spreadsheetId)}`;
      const dlResp = await fetch(`/api/projects/${projectId}/exports/consolidated-sheets/download${qs}`, {
        method: 'GET',
        credentials: 'include' as RequestCredentials,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!dlResp.ok) throw new Error('Download failed for consolidated sheet');

      const clientName = valueSummary?.kickoff?.clientName || 'Value_Assessment';
      const safeName = clientName.replace(/[^a-zA-Z0-9]/g, '_');
      const dateStamp = new Date().toISOString().split('T')[0];
      const fallbackName = `${safeName}_Consolidated_Savings_${dateStamp}.xlsx`;

      const cd = dlResp.headers.get('content-disposition') || '';
      const filenameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
      const rawName = decodeURIComponent(filenameMatch?.[1] || filenameMatch?.[2] || '');
      const filename = rawName || fallbackName;

      const blob = await dlResp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Consolidated Savings Ready',
        description: `Downloaded ${filename}. ${webViewLink ? 'Google Sheet also created.' : ''}`,
        duration: 20000,
        action: webViewLink ? (
          <ToastAction asChild altText="Open Sheet">
            <a href={webViewLink} target="_blank" rel="noopener noreferrer">Open Sheet</a>
          </ToastAction>
        ) : undefined,
      });
    } catch (error: any) {
      console.error('❌ Consolidated Savings export failed:', error);
      toast({
        title: 'Export Failed',
        description: `Consolidated export error: ${error?.message || 'Unknown error'}`,
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setIsConsolidatedExporting(false);
    }
  }, [projectId, valueSummary, toast]);

  // Helper functions to create tables programmatically
  const createFiveYearTable = () => {
    const table = document.createElement('div')
    table.className = 'space-y-2 p-4 bg-white rounded-lg border'
    table.innerHTML = `
      <h3 class="text-lg font-semibold mb-3">Five-Year Value Projection Details</h3>
      <table class="w-full border-collapse">
        <thead>
          <tr class="border-b">
            <th class="text-left p-2">Year</th>
            <th class="text-right p-2">Annual Savings</th>
            <th class="text-right p-2">Cumulative Value</th>
          </tr>
        </thead>
        <tbody>
          ${fiveYearData.map((year, index) => `
            <tr class="border-b">
              <td class="p-2">Year ${index + 1}</td>
              <td class="p-2 text-right">$${(year.total / 1000).toFixed(0)}K</td>
              <td class="p-2 text-right">$${((year.total * (index + 1)) / 1000).toFixed(0)}K</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
    return table
  }

  // Waterfall table function removed - not needed

  /**
   * ===========================================================================================
   * REGIONAL PILLAR TABLE CREATOR - DYNAMIC TABLE GENERATION FOR EXPORTS
   * ===========================================================================================
   * 
   * PURPOSE:
   * Generates HTML table elements showing task-level breakdown for each pillar.
   * Used during high-quality image exports to provide detailed data alongside charts.
   * 
   * MATCHING LOGIC:
   * 1. Exact pillar name match (primary)
   * 2. Partial match for flexibility (fallback)
   * 3. Special case handling for "Annual Savings" showing top 5 tasks
   * 
   * TABLE STRUCTURE:
   * - Header: Task | FTEs | Annual Savings | Automation %
   * - Body: Individual task rows with alternating colors
   * - Footer: Totals row with regional distribution summary
   * 
   * BUSINESS CONTEXT:
   * These tables are exported as images and embedded in PowerPoint presentations
   * to provide detailed breakdowns that complement the chart visualizations.
   */
  const createRegionalPillarTable = (pillarName: string) => {
    // Find regional data for this pillar - more flexible matching
    let pillarTasks = (taskSavingsData || []).filter(task => {
      const taskPillar = task.pillar || ''
      const nameMatch = taskPillar === pillarName
      const partialMatch = pillarName.toLowerCase().includes(taskPillar.toLowerCase()) || 
                          taskPillar.toLowerCase().includes(pillarName.toLowerCase())
      return nameMatch || partialMatch
    })
    
    // If no exact match, try broader matching for common cases
    if (pillarTasks.length === 0) {
      if (pillarName.includes('Account Reconciliation')) {
        pillarTasks = (taskSavingsData || []).filter(task => task.pillar === 'Account Reconciliation')
      } else if (pillarName.includes('Annual Savings')) {
        // For the general annual savings chart, show top 5 tasks
        pillarTasks = (taskSavingsData || []).slice(0, 5)
      }
    }

    const table = document.createElement('div')
    table.className = 'space-y-2 p-4 bg-white rounded-lg border'
    
    // Create the table with more robust content
      table.innerHTML = `
      <h3 class="text-lg font-semibold mb-3 text-black">${pillarName} Regional Breakdown</h3>
      <table class="w-full border-collapse border border-gray-300">
        <thead class="bg-gray-100">
          <tr class="border-b-2 border-gray-300">
            <th class="text-left p-3 border-r border-gray-300 font-bold text-black">Task</th>
            <th class="text-right p-3 border-r border-gray-300 font-bold text-black">FTEs</th>
            <th class="text-right p-3 border-r border-gray-300 font-bold text-black">Annual Savings</th>
            <th class="text-right p-3 font-bold text-black">Automation %</th>
          </tr>
        </thead>
        <tbody>
          ${pillarTasks.length > 0 ? pillarTasks.map((task, index) => `
            <tr class="border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}">
              <td class="p-3 border-r border-gray-200 text-black">${task.taskName}</td>
              <td class="p-3 text-right border-r border-gray-200 text-black">${task.ftesImpacted ?? '—'}</td>
              <td class="p-3 text-right border-r border-gray-200 text-black">$${task.annualSavings ? (task.annualSavings / 1000).toFixed(0) + 'K' : '—'}</td>
              <td class="p-3 text-right text-black">${task.automationPercent ? task.automationPercent + '%' : '—'}</td>
            </tr>
          `).join('') : `
            <tr class="border-b border-gray-200">
              <td colspan="4" class="p-3 text-center text-black">No data available for ${pillarName}</td>
            </tr>
          `}
          <tr class="bg-blue-50 border-t-2 border-blue-300">
            <td class="p-3 font-bold text-black border-r border-gray-200">Total</td>
            <td class="p-3 text-right font-bold text-black border-r border-gray-200">${pillarTasks.reduce((sum, task) => sum + (task.ftesImpacted ?? 0), 0)}</td>
            <td class="p-3 text-right font-bold text-black border-r border-gray-200">$${(pillarTasks.reduce((sum, task) => sum + (task.annualSavings ?? 0), 0) / 1000).toFixed(0)}K</td>
            <td class="p-3 text-right font-bold text-black">-</td>
          </tr>
        </tbody>
      </table>
      <div class="mt-4 p-3 bg-blue-50 rounded border text-sm text-black">
        <strong>Regional Distribution:</strong> 
        Americas (${regionalFTEData.find(r => r.region === 'Americas')?.ftes ?? '—'} FTEs), 
        EMEA (${regionalFTEData.find(r => r.region === 'EMEA')?.ftes ?? '—'} FTEs), 
        APAC (${regionalFTEData.find(r => r.region === 'APAC')?.ftes ?? '—'} FTEs), 
        India (${regionalFTEData.find(r => r.region === 'India')?.ftes ?? '—'} FTEs), 
        Shared Services (${regionalFTEData.find(r => r.region === 'Shared Services')?.ftes ?? '—'} FTEs)
      </div>
    `
    // Table created for ${pillarName} with ${pillarTasks.length} tasks
    
    return table
  }

  // Generate public share link
  const handleGenerateShareLink = async () => {
    if (isPublicView) {
      toast({
        title: "Share Not Available",
        description: "Share functionality is not available in public view.",
        variant: "destructive",
        duration: 3000,
      })
      return
    }

    setIsSharing(true)
    
    try {
      const token = typeof window !== 'undefined' ? window.localStorage.getItem('jwt_token') : null;
      const response = await fetch(`/api/projects/${projectId}/generate-public-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          expires_in_days: 365 * 10  // 10 years - effectively permanent
        })
      })
      
      if (!response.ok) {
        throw new Error('Failed to generate share link')
      }
      
      const data = await response.json()
      setShareUrl(data.publicUrl)
      setShowShareModal(true)
      
      toast({
        title: "Share Link Generated",
        description: "Public link created successfully. Permanent access link.",
        duration: 3000,
      })
    } catch (error) {
      console.error('Error generating share link:', error)
      toast({
        title: "Share Link Failed",
        description: "Failed to generate share link. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
    } finally {
      setIsSharing(false)
    }
  }
    const avgBenchmarkForPillar = React.useCallback((pillarName: string) => {
        const list = automationBenchmarksFromQuery || [];
        if (!pillarName || !Array.isArray(list) || list.length === 0) return 0;

        const key = normalizePillarName(pillarName)?.toLowerCase().trim();
        if (!key) return 0;

        const matches = list.filter((b: any) => {
            const normalized = normalizePillarName(b.pillar || b.name || "")?.toLowerCase().trim();
            return normalized === key;
        });

        if (matches.length === 0) return 0;

        const sum = matches.reduce((s: number, b: any) => {
            const v = Number(b.benchmark_percent ?? b.benchmarkAutomation ?? b.percent ?? 0);
            return s + (Number.isFinite(v) ? v : 0);
        }, 0);

        return sum / matches.length;
}, [automationBenchmarksFromQuery]);

  /**
   * ===========================================================================================
   * MAIN COMPONENT RENDER FUNCTION
   * ===========================================================================================
   * 
   * PURPOSE:
   * Renders the complete Cost Savings Summary interface with responsive design,
   * executive-grade visualizations, and comprehensive export capabilities.
   * 
   * LAYOUT STRUCTURE:
   * 1. Strategic Value Summary - Executive KPI dashboard
   * 2. Financial Summary - Investment and ROI analysis (pricing enabled only)
   * 3. Annual Savings Forecast - Five-year projection with pillar breakdown
   * 4. Regional Analysis - Geographic distribution charts
   * 5. Pillar-Specific Charts - Individual R2R process analysis  
   * 6. Waterfall Analysis - Automation gap opportunity
   * 7. Benchmark Gap Insights - Industry comparison
   * 8. Export Controls - CSV, PowerPoint, and sharing options
   * 
   * RESPONSIVE DESIGN:
   * - Mobile-first approach with progressive enhancement
   * - Flexible spacing (space-y-3 sm:space-y-4 md:space-y-6)
   * - Adaptive padding (py-3 sm:py-4 px-3 sm:px-4 md:px-6)
   * - Maximum width constraint (max-w-7xl) for large screens
   */
  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto space-y-3 sm:space-y-4 md:space-y-6 py-3 sm:py-4 px-3 sm:px-4 md:px-6">
        
        {/**
         * ===========================================================================================
         * SECTION 1: STRATEGIC VALUE SUMMARY
         * ===========================================================================================
         * 
         * PURPOSE:
         * Executive dashboard showing high-level KPIs and strategic context.
         * Provides immediate value overview for C-suite presentations.
         * 
         * COMPONENTS:
         * - Client name and project context
         * - StrategicSummary: Annual savings, five-year value, FTE totals
         * - FinancialSummary: ROI analysis (conditional on pricing enabled)
         * 
         * EXPORT INTEGRATION:
         * - data-export-section-id attributes for PowerPoint capture
         * - CSS classes for chart/table export functionality
         */}
        <div className="section-wrapper mb-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold bg-forestBlack/50 border-[#334155] flex items-center gap-2">
                <IconTile icon={DollarSign} color="primary" bg="background" size={36} iconSize={36} />
              Strategic Value Summary
            </h2>
            <p className="text-sm text-slate-600 dark:text-muted-foreground">
              {summaryData?.kickoff?.clientName || 'Value Assessment'} • Cost Savings Analysis
            </p>
            
            {/* Selected Pillars Display */}
            {summaryData?.valueSummary?.selectedPillars && summaryData.valueSummary.selectedPillars.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {summaryData.valueSummary.selectedPillars.map((pillar: string) => (
                  <span 
                    key={pillar} 
                    className="selected-pillar-badge px-3 py-1 rounded-full text-xs font-medium"
                  >
                    {pillar}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Strategic Summary Card with Export Configuration */}
          <div 
            id="capture-kpis-summary" 
            className="strategic-summary" 
            data-testid="strategic-summary-section"
            data-export-section-id="StrategicSummary"
          >
            {(() => {
                // CRITICAL: Strategic Summary must respect toggle state
// Toggle OFF = show ALL activities (granular) = HIGHER values
// Toggle ON = show selected pillars only (filtered) = LOWER values
                const activitiesForCalculation = pillarFilteringEnabled ? filteredActivityBreakdown : granularActivityBreakdown;

                const pillarFTEAllocation =
                    summaryData?.taskFTEAllocation?.pillarFTEAllocation ??
                    {};

                const weightedAutomation = computeMixedWeightedAutomation(
                    chartDataResult.granularActivityBreakdown,
                    pillarFTEAllocation,
                    automationBenchmarksQuery.data
                );

                const clientWeighted = weightedAutomation.clientWeighted;
                const benchmarkWeighted = weightedAutomation.benchmarkWeighted;
                const totalFTEsMixed = weightedAutomation.totalFTEsMixed;

                const hasStableAutomationData = totalFTEsMixed > 0;

                if (!hasStableAutomationData) {
                    return null; // Don't render until automation calculations are stable
                }

        // Calculate benchmark savings using the SAME filtered data as Strategic Summary
        // (unchanged logic — respects toggle state)
                const benchmarkSavings = (
                    activitiesForCalculation &&
                    activitiesForCalculation.length > 0 &&
                    automationBenchmarksQuery.isSuccess &&
                    (automationBenchmarksFromQuery?.length || 0) > 0
                )
                    ? calculateBenchmarkAutomationSavings(activitiesForCalculation, automationBenchmarksFromQuery)
                    : undefined;

            // Toggle-aware totals and savings from filtered activities (unchanged)
                const toggleAwareAnnualSavings = activitiesForCalculation.reduce((sum, activity) => {
                    return sum + (activity.annualSavings || 0);
                }, 0);



              return (
                <StrategicSummary 
                  annualSavings={toggleAwareAnnualSavings}
                  totalFTEs={totalFTEsMixed}
                  benchmarkSavings={benchmarkSavings}
                  validatedAutomation={clientWeighted}
                  benchmarkAutomation={benchmarkWeighted}
                />
              );
            })()}
          </div>

          {/* 
            ===========================================================================================
            FINANCIAL SUMMARY - CONDITIONAL RENDERING
            ===========================================================================================
            
            PURPOSE:
            Displays ROI, NPV, and Payback Period metrics when pricing is enabled.
            This section is controlled by the pricing toggle functionality.
            
            VISIBILITY CONDITIONS:
            - showFinancials === true (from parent prop)
            - summaryData?.valueSummary exists (data availability check)
            
            BUSINESS LOGIC:
            When pricing is disabled:
            - This section is completely hidden
            - Only cost savings are shown (no investment/return metrics)
            - Used for "Value Assessment Only" presentations
            
            When pricing is enabled:
            - Shows complete financial analysis
            - Includes investment breakdown
            - Displays ROI calculations
            - Used for full business case presentations
            
            DATA DEPENDENCIES:
            - valueSummary: Contains pricing and investment data
            - annualSavings: Normalized savings from granular calculations
            - data: Raw database data for additional metrics
            
            EXPORT BEHAVIOR:
            - Captured as "FinancialSummary" section in PowerPoint
            - Only included in exports when pricing is enabled
            - Maintains light theme during export regardless of UI theme
          */}
          {(() => {
            return showFinancials && summaryData?.valueSummary ? (
              <div 
                id="capture-investment-timeline" 
                className="financial-summary-component mt-6" 
                data-testid="financial-summary-section"
                data-export-section-id="FinancialSummary"
              >
                <FinancialSummary 
                  valueSummary={summaryData.valueSummary} 
                  annualSavings={annualSavings}
                  discountSoftwarePercent={discountSoftwarePercent}
                  discountServicesPercent={discountServicesPercent}
                  showDiscountSliders={showDiscountSliders}
                  onToggleDiscountSliders={onToggleDiscountSliders}
                  onDiscountSoftwareChange={onDiscountSoftwareChange}
                  onDiscountServicesChange={onDiscountServicesChange}
                  currentRevenueBand={currentRevenueBand}
                  currentEntityCount={currentEntityCount}
                  currentSelectedPillars={currentSelectedPillars}
                  yearlyCosts={projectYearlyCosts}
                />
              </div>
            ) : null;
          })()}
        </div>



        {/* Savings Forecast */}
        <div id="capture-pillar-savings-and-automation" className="section-wrapper">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-foreground flex items-center gap-2">
                <IconTile icon={TrendingUp} color="primary" bg="background" size={36} iconSize={36} />
              Savings Forecast
            </h2>
            
            {/* Pillar Filtering Toggle*/}
            {/*<div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800 p-3 rounded-lg border">*/}
            {/*  <Filter className="w-4 h-4 text-slate-600 dark:text-slate-400" />*/}
            {/*  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">*/}
            {/*    Filter by Selected Pillars*/}
            {/*  </span>*/}
            {/*  <Switch*/}
            {/*    checked={pillarFilteringEnabled}*/}
            {/*    onCheckedChange={setPillarFilteringEnabled}*/}
            {/*    className="data-[state=checked]:bg-teal-600"*/}
            {/*  />*/}
            {/*  {pillarFilteringEnabled ? (*/}
            {/*    <span className="text-xs text-teal-600 dark:text-teal-400 font-medium">*/}
            {/*      Showing {consolidatedSelectedPillars.length} selected pillars only*/}
            {/*    </span>*/}
            {/*  ) : (*/}
            {/*    <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">*/}
            {/*      Showing all activities*/}
            {/*    </span>*/}
            {/*  )}*/}
            {/*</div>*/}
          </div>
          <Card 
            className="border-2 border-teal-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/80"
            id="capture-savings-timeline" 
            data-testid="five-year-projection-chart"
            data-export-section-id="FiveYearProjection"
            aria-label="Savings Forecast"
          >
            <CardHeader className="px-0 pt-0">


          </CardHeader>
          <CardContent>
            <div id="capture-cumulative-savings-chart-wrapper">
              <div 
                id="section-pillar-savings"
                data-testid="value-forecast-chart" 
                data-export-section-id="AnnualCumulativeSavings"
                className="h-96 sm:h-[30rem] md:h-[36rem] w-full pt-20"
              >
                <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={(() => {
                    // CRITICAL: Use filtered breakdown for Savings Forecast section
                    // This respects the pillar filtering toggle unlike Financial Summary
                    const breakdownData = pillarFilteringEnabled ? filteredActivityBreakdown : granularActivityBreakdown
                    const rampByPillar = (
                      (summaryData as any)?.valueSummary?.rampByPillarYear ||
                      (summaryData as any)?.rampByPillarYear ||
                      {}
                    ) as Record<string, number[]>
                    
                    // Build 5-year projection data from filtered activity breakdown
                    const pillarSavings = breakdownData.reduce((acc, activity) => {
                      if (!acc[activity.pillar]) {
                        acc[activity.pillar] = 0
                      }
                      acc[activity.pillar] += activity.annualSavings
                      return acc
                    }, {} as Record<string, number>)

                    // Generate 5 years of data with annual savings + per-pillar ramp and cumulative line
                    let cumulativeRunningTotal = 0
                    return Array.from({length: 5}, (_, yearIndex) => {
                      const year = yearIndex + 1
                      // Build base (fixed-typed fields) and dynamic pillar fields separately to avoid index signature conflicts
                      const base = {
                        year: `Year ${year}`,
                        cumulativeTotal: 0 // Will be calculated below
                      }
                      
                      // Annual savings for bars (not cumulative)
                      const pillars: Record<string, number> = {}
                      Object.entries(pillarSavings).forEach(([pillar, annualSavings]) => {
                        const ramp = Number(rampByPillar?.[pillar]?.[yearIndex]);
                        const rampMultiplier = Number.isFinite(ramp) ? Math.max(0, Math.min(100, ramp)) / 100 : 1;
                        pillars[pillar] = annualSavings * rampMultiplier
                      })
                      
                      // Calculate cumulative total for line
                      const totalAnnualSavings = Object.values(pillars).reduce((sum, val) => sum + val, 0)
                      const result = { ...base, ...pillars }
                      cumulativeRunningTotal += totalAnnualSavings
                      result.cumulativeTotal = cumulativeRunningTotal
                      
                      return result
                    })
                  })()}
                  margin={{ top: 20, right: 70, left: 70, bottom: 50 }}
                >
                  <CartesianGrid 
                    strokeDasharray="2 4" 
                    stroke="#FFFFFF"
                    opacity={0.15}
                    vertical={false}
                  />
                  <XAxis 
                    dataKey="year" 
                    stroke="#FFFFFF"
                    fontSize={13}
                    fontWeight={600}
                    tickLine={{ stroke: '#FFFFFF', strokeWidth: 2 }}
                    axisLine={{ stroke: '#FFFFFF', strokeWidth: 2 }}
                  />
                  <YAxis 
                    yAxisId="left"
                    stroke="#FFFFFF"
                    fontSize={11}
                    fontWeight={500}
                    padding={{ bottom: 2 }}
                    tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                    tickLine={{ stroke: '#FFFFFF', strokeWidth: 1.5 }}
                    axisLine={{ stroke: '#FFFFFF', strokeWidth: 5 }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    stroke="#FFFFFF"
                    fontSize={11}
                    fontWeight={500}
                    tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                    tickLine={{ stroke: '#FFFFFF', strokeWidth: 1.5 }}
                    axisLine={{ stroke: '#FFFFFF', strokeWidth: 2 }}
                  />
                  <RechartsTooltip 
                    contentStyle={{
                      backgroundColor: '#86159f',
                      border: '2px solid #1A9B8A',
                      borderRadius: '12px',
                      color: '#F9FAFB',
                      boxShadow: '0 8px 25px rgba(26, 155, 138, 0.25)',
                      backdropFilter: 'blur(8px)'
                    }}
                    formatter={(value: number, name: string, props: any) => {
                      if (name === 'Cumulative Total') {
                        return [`$${value.toLocaleString()}`, 'Cumulative 5-Year Value']
                      }
                      // Enhanced tooltip for pillar data with more context
                      return [
                        `$${value.toLocaleString()}`,
                        `${name} (Annual Savings)`
                      ]
                    }}
                    labelFormatter={(label: string) => `${label} - Savings Breakdown`}
                    labelStyle={{ color: '#F9FAFB', fontWeight: 'bold' }}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const yearData = payload[0]?.payload
                        const pillarData = payload.filter(entry => entry.dataKey !== 'cumulativeTotal')
                        const cumulativeData = payload.find(entry => entry.dataKey === 'cumulativeTotal')
                        
                        return (
                          <div className="chart-tooltip-container">
                            <p className="text-white font-semibold mb-2">{label} - Savings Breakdown</p>
                            
                            {/* Show pillar breakdown */}
                            {pillarData.map((entry, index) => (
                              <div key={index} className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="legend-color-indicator" 
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-gray-300 text-sm">{entry.name}:</span>
                                </div>
                                <span className="text-white font-medium">${entry.value?.toLocaleString()}</span>
                              </div>
                            ))}
                            
                            {/* Show cumulative total if available */}
                            {cumulativeData && (
                              <div className="border-t border-gray-600 mt-2 pt-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-300 text-sm font-medium">Cumulative Total:</span>
                                  <span className="text-primary font-bold">${cumulativeData.value?.toLocaleString()}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ 
                      paddingTop: '16px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    iconType="circle"
                    content={({ payload }) => {
                      // CRITICAL: Use same breakdown for legend as chart bars (respects pillar toggle)
                      const legendBreakdownData = pillarFilteringEnabled ? filteredActivityBreakdown : granularActivityBreakdown
                      const pillarsWithData = getPillarsWithData(legendBreakdownData)
                      const allPillarNames = [...PILLAR_NAMES]
                      
                      // Create persistent legend items for all pillars with data
                      const legendItems = pillarsWithData.map(pillar => ({
                        dataKey: pillar,
                        value: pillar,
                        color: getPillarColor(pillar)
                      }))
                      
                      const excludedPillars = allPillarNames.filter(pillar => !pillarsWithData.includes(pillar))
                      
                      return (
                        <div className="mt-4">
                          <div className="flex flex-wrap justify-center gap-2">
                            {legendItems.map((entry, index) => {
                              const pillarId = entry.dataKey.toLowerCase().replace(/\s+/g, '-')
                              const isVisible = visiblePillars[entry.dataKey] // Default to true if not set
                              return (
                                <div
                                  key={`legend-${entry.dataKey}`}
                                  className={`flex items-center gap-2 px-3 py-1 rounded-full border transition-all cursor-pointer ${
                                    isVisible 
                                      ? 'legend-item-active' 
                                      : 'legend-item-inactive'
                                  }`}
                                  data-testid={`legend-item-${pillarId}`}
                                  onClick={() => togglePillarVisibility(entry.dataKey)}
                                >
                                  <div
                                    className="legend-color-indicator rounded-full"
                                    style={{ backgroundColor: entry.color, opacity: isVisible ? 1 : 0.4 }}
                                  />
                                  <span className={`text-sm font-medium ${isVisible ? 'text-slate-200' : 'text-slate-500'}`}>
                                    {entry.value}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                          {excludedPillars.length > 0 && (
                            <div className="mt-2 text-center">
                              <span className="text-xs text-slate-500">
                                Excluded: {excludedPillars.join(', ')} (no data in this scenario)
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  
                  {/* Dynamic bars for each pillar with specified colors - conditionally rendered */}
                  {(() => {
                    // CRITICAL: Use filtered breakdown for chart bars (respects pillar toggle)
                    const chartBreakdownData = pillarFilteringEnabled ? filteredActivityBreakdown : granularActivityBreakdown
                    const pillarsWithData = getPillarsWithData(chartBreakdownData)
                    const pillars = [...PILLAR_NAMES]
                    
                    return pillars.map((pillar) => {
                      const color = getPillarColor(pillar)
                      // Only render if pillar has data AND is visible (default to true if not set)
                      const isVisible = visiblePillars[pillar] // Default to true if not explicitly set to false
                      if (pillarsWithData.includes(pillar) && isVisible) {
                        const pillarId = pillar.toLowerCase().replace(/\s+/g, '-')
                        return (
                          <Bar 
                            key={pillar}
                            yAxisId="left" 
                            dataKey={pillar} 
                            fill={color} 
                            maxBarSize={60}
                            radius={[4, 4, 0, 0]}
                            stroke={color}
                            strokeWidth={1}
                            data-testid={`chart-series-${pillarId}`}
                          />
                        )
                      }
                      return null
                    }).filter(Boolean);
                  })()}
                  
                  {/* Cumulative line overlay */}
                  <Area 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="cumulativeTotal" 
                    stroke="#FFFFFF"
                    strokeWidth={4}
                    fill="url(#cumulativeGradient)"
                    name="Cumulative Total"
                    dot={{ fill: '#FFFFFF', strokeWidth: 3, r: 5 }}
                    activeDot={{ r: 8, fill: '#FFFFFF', strokeWidth: 2, stroke: '#CCCCCC' }}
                  />
                  
                  {/* Gradient definition for cumulative area */}
                  <defs>
                    <linearGradient id="cumulativeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1A9B8A" stopOpacity={0.3}/>
                      <stop offset="100%" stopColor="#1A9B8A" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

            {/* Summary Row Under Chart */}
            <div className="mt-6 py-4 border-t border-border/50">
              <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-6">
                  
                  
                  
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowActivityBreakdown(!showActivityBreakdown)}
                  className="flex items-center gap-2 "
                >
                  {showActivityBreakdown ? (
                    <>
                      Collapse full activity breakdown <ChevronUp className="ml-1 h-4 w-4 text-slate-700 dark:text-slate-300" />
                    </>
                  ) : (
                    <>
                      View full activity breakdown <ChevronDown className="ml-1 h-4 w-4 text-slate-700 dark:text-slate-300" />
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Activity Breakdown Table */}
            {showActivityBreakdown && (
              <div data-testid="activity-breakdown-expanded" id="capture-savings-table" className="mt-4">
                {(() => {
                  // Use filtered activity breakdown for Savings Forecast table (respects pillar filtering toggle)
                  const activitiesForTable = filteredActivityBreakdown.length > 0 ? filteredActivityBreakdown : granularActivityBreakdown
                  // Group activities by pillar and reorder so Other Processes is last
                  const pillarSavings = activitiesForTable.reduce((acc, activity) => {
                    const pillar = normalizePillar(activity.pillar)
                    if (!acc[pillar]) {
                      acc[pillar] = { annualSavings: 0, activities: [] }
                    }
                    acc[pillar].annualSavings += activity.annualSavings
                    acc[pillar].activities.push(activity)
                    return acc
                  }, {} as Record<string, { annualSavings: number, activities: any[] }>)

                  // Reorder pillars to put Other Processes last
                  const orderedPillars = Object.keys(pillarSavings).sort((a, b) => {
                    if (a === 'Other Processes') return 1
                    if (b === 'Other Processes') return -1
                    return 0
                  })

                  // Use shared pillar colors
                  const getColorFor = (p: string) => getPillarColor(p)

                  // Filter pillars to only show those with meaningful data AND are visible
                  const pillarsWithData = getPillarsWithData(activitiesForTable)
                  const filteredPillars = orderedPillars.filter(pillar => {
                    const hasData = pillarsWithData.includes(pillar)
                    const isVisible = visiblePillars[pillar] // Default to true if not explicitly set to false
                    return hasData && isVisible
                  })
                  
                  return (
                    <div className="space-y-8">
                      {filteredPillars.map((pillar) => {
                        const data = pillarSavings[pillar]
                        const color = getColorFor(pillar) || '#6B7280'
                        return (
                          <div key={pillar} data-testid={`pillar-table-${pillar.toLowerCase().replace(/\s+/g, '-')}`} className="border-l-4 pillar-section" style={{ borderLeftColor: color }} >
                            {/* Collapsible Pillar Header */}
                            <div 
                              className="flex items-center justify-between cursor-pointer mb-4"
                              onClick={() => togglePillar(pillar)}
                            >
                              <div className="flex items-center gap-4">
                                <div 
                                  className="pillar-header-indicator"
                                  style={{ backgroundColor: color }}
                                />
                                <h3 className="text-xl font-semibold text-slate-200 tracking-tight">{pillar}</h3>
                                <Badge 
                                  variant="secondary" 
                                  className="px-3 py-1 badge-secondary"
                                >
                                  {data.activities.length} activities
                                </Badge>
                              </div>
                              <ChevronDown className={`h-5 w-5 text-slate-400 transform transition-transform duration-200 ${
                                expandedPillars[pillar] ? 'rotate-180' : ''
                              }`} />
                            </div>
                            {/* Per-Pillar Summary Block */}
                            {(() => {
                              // Calculate pillar totals from granular breakdown (PRIMARY SOURCE)
                              const granularActivitiesForPillar = granularActivityBreakdown.filter(activity => normalizePillar(activity.pillar) === pillar)
                              
                              // Use granular breakdown as primary source, with fallback to pillar activities
                              const granularAnnual = granularActivitiesForPillar.reduce((sum, activity) => {
                                return sum + (activity.annualSavings || 0)
                              }, 0)
                              
                              // Fallback to pillar activities if no granular data available
                              const activities = data.activities || []
                              const pillarAnnual = activities.reduce((sum, activity) => {
                                const savingsValue = activity.annualSavings || activity.savings
                                return sum + (savingsValue ?? 0)
                              }, 0)
                              
                              // Use granular data if available, otherwise use pillar data
                              const totalAnnual = granularActivitiesForPillar.length > 0 ? granularAnnual : pillarAnnual
                              
                              // FTE calculation: use granular if available, otherwise pillar data
                              const totalFTEs = granularActivitiesForPillar.length > 0 
                                ? granularActivitiesForPillar.reduce((sum, activity) => sum + (activity.totalFTEs || 0), 0)
                                : activities.reduce((sum, activity) => {
                                    const fteValue = activity.ftesImpacted || activity.totalFTEs || activity.ftes
                                    return sum + (fteValue ?? 0)
                                  }, 0)

                              // Calculate weighted automation percentage
                              const weightedAutomationSum = activities.reduce((sum, activity) => {
                                const ftes = activity.ftesImpacted || activity.totalFTEs || activity.ftes
                                const automation = activity.automationPercent || activity.automationPercentage || activity.automation
                                return sum + ((ftes ?? 0) * (automation ?? 0))
                              }, 0)
                              const deliverableAutomation = totalFTEs > 0 ? Math.round(weightedAutomationSum / totalFTEs) : 0
                              // CRITICAL: Must use actual benchmark data from database, no hardcoded additions
                              // const benchmarkAutomation = 0 // TODO: Calculate from actual benchmark data
                                const benchmarkAutomation = avgBenchmarkForPillar(pillar);

                              if (totalFTEs === 0 && totalAnnual === 0) {
                                return null // Don't show summary if no data
                              }
                              
                              return (
                                <div 
                                  data-testid={`pillar-summary-header-${pillar.toLowerCase().replace(/\s+/g, '-')}`}
                                  className="mt-8 mb-6 px-4"
                                >
                                  {/* IMPROVED METRICS LAYOUT - Two Column Grid */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Left Column - Core Metrics */}
                                    <div className="space-y-3">
                                      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                                        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Workforce Impact</div>
                                        <div className="flex items-baseline gap-2 mb-3">
                                          <span className="text-2xl font-bold text-white">
                                               {Number(totalFTEs).toFixed(2)}
                                          </span>
                                          <span className="text-sm text-slate-400">FTEs</span>
                                        </div>
                                        <div className="text-sm">
                                          <span className="text-slate-400">Total Addressable Hours:</span>{' '}
                                          <span className="text-amber-400 font-semibold">{Math.round((totalFTEs * TIME_CONFIG.HOURS_PER_FTE_YEAR) / 1000)}k hrs/year</span>
                                        </div>
                                      </div>
                                      
                                      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                                        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Deliverable Savings</div>
                                        <div className="flex items-baseline gap-2">
                                          <span className="text-2xl font-bold text-teal-400">{formatCurrency(totalAnnual)}</span>
                                        </div>
                                        <div className="text-xs text-slate-400 mt-1">Annual deliverable savings</div>
                                      </div>
                                    </div>
                                    
                                    {/* Right Column - Automation Metrics */}
                                    <div className="space-y-3">
                                      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                                        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Automation Performance</div>
                                        
                                        <div className="space-y-3">
                                          <div className="flex items-center justify-between">
                                            <span className="text-sm text-slate-300">Deliverable Rate</span>
                                            <div className="flex items-center gap-2">
                                              <div className="w-20 bg-slate-700 rounded-full h-2">
                                                <div 
                                                  className="bg-emerald-500 h-2 rounded-full transition-all duration-300" 
                                                  style={{width: `${Math.min(deliverableAutomation, 100)}%`}}
                                                ></div>
                                              </div>
                                              <span className="text-emerald-400 font-bold text-sm w-10 text-right">{deliverableAutomation}%</span>
                                            </div>
                                          </div>
                                          
                                          <div className="flex items-center justify-between">
                                            <span className="text-sm text-slate-300">Benchmark Rate</span>
                                            <div className="flex items-center gap-2">
                                              <div className="w-20 bg-slate-700 rounded-full h-2">
                                                <div 
                                                  className="bg-redwoodLightBlue h-2 rounded-full transition-all duration-300"
                                                  style={{width: `${Math.min(benchmarkAutomation, 100)}%`}}
                                                ></div>
                                              </div>
                                              <span className="text-redwoodLightBlue font-bold text-sm w-10 text-right">{benchmarkAutomation}%</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* New Hours Saved Card */}
                                      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                                        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Hours Saved</div>
                                        <div className="flex items-baseline gap-2 mb-3">
                                          <span className="text-2xl font-bold text-emerald-400">{Math.round((totalFTEs * (deliverableAutomation / 100) * TIME_CONFIG.HOURS_PER_FTE_YEAR) / 1000)}k</span>
                                          <span className="text-sm text-slate-400">hrs/year</span>
                                        </div>
                                        <div className="text-xs text-slate-400 mt-1">Automation-adjusted capacity</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                            {/* Enhanced Activity-Level Table - Collapsible */}
                            {expandedPillars[pillar] && (
                            <div className="overflow-x-auto px-4">
                                <div className="rounded-lg overflow-hidden border border-separate border-slate-700/50 bg-slate-800/30">
                                    <table
                                        data-testid="activity-table-final"
                                        className="activity-table w-full shadow-sm "
                                    >
                                        <thead>
                                        <tr className="activity-table-header border-b border-slate-700/50">
                                            <th className="text-left py-5 px-6 text-sm font-semibold text-gray-300 uppercase tracking-wide bg-slate-800/50">Task</th>
                                            <th className="text-center py-5 px-6 text-sm font-semibold text-gray-300 uppercase tracking-wide bg-slate-800/50">FTEs</th>
                                            <th className="text-right text-sm font-semibold text-gray-300 uppercase tracking-wide bg-slate-800/50 py-5 pr-3">
                                                Automation %
                                            </th>
                                            <th className="text-right text-sm font-semibold text-gray-300 uppercase tracking-wide bg-slate-800/50 py-5 pr-3">Hours
                                                Saved
                                            </th>
                                            <th className="text-right text-sm font-semibold text-gray-300 uppercase tracking-wide bg-slate-800/50 py-5 pr-3">
                                                Savings ($)
                                            </th>
                                            <th className="text-center py-5 px-4 text-sm font-semibold text-gray-300 uppercase tracking-wide bg-slate-800/50 w-16">
                                                <span className="sr-only">Expand Details</span>
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {(() => {
                                            if (!data.activities || data.activities.length === 0) {
                                                return (
                                                    <tr>
                                                        <td colSpan={6}
                                                            className="py-8 px-4 text-center text-slate-400">
                                                            No activities found for {pillar}
                                                        </td>
                                                    </tr>
                                                )
                                            }

                                            const filteredActivities = data.activities.filter(activity => {
                                                const hasFTEs = (activity.ftesImpacted || activity.totalFTEs || activity.ftes || 0) > 0
                                                const hasSavings = (activity.annualSavings || activity.savings || 0) > 0

                                                return hasFTEs && hasSavings
                                            })

                                            if (filteredActivities.length === 0) {
                                                return (
                                                    <tr>
                                                        <td colSpan={6}
                                                            className="py-8 px-4 text-center text-slate-400">
                                                            No activities with FTEs and savings found for {pillar}
                                                        </td>
                                                    </tr>
                                                )
                                            }

                                            return filteredActivities.map((activity, activityIndex) => (
                                                <React.Fragment key={activityIndex}>
                                                    <tr
                                                        data-testid={`activity-row-${(activity.activityName || activity.taskName || 'unnamed').toLowerCase().replace(/\s+/g, '-')}`}
                                                        className={`activity-table-row ${
                                                            activity.ftesImpacted === 0 || activity.annualSavings === 0
                                                                ? 'opacity-50'
                                                                : ''
                                                        }`}
                                                    >
                                                        {/* Column 1: Task Name */}
                                                        <td className="py-4 px-4 text-white font-medium text-sm">
                                                            <div className="whitespace-normal break-words">
                                                                {activity.activityName || activity.taskName || activity.name || activity.task || activity.label || "Unnamed Activity"}
                                                            </div>
                                                        </td>

                                                        {/* Column 2: FTEs (by region) - Regional breakdown pills */}
                                                        <td className="py-4 px-6">
                                                            {/*<div className="flex flex-wrap gap-1 w-full break-words whitespace-normal">*/}
                                                            <div
                                                                className={`flex flex-wrap gap-1 w-full break-words whitespace-normal ${
                                                                    Object.entries((activity.regionBreakdown || activity.regionalFTEs || activity.fteBreakdown || activity.regions || {}) as Record<string, number>)
                                                                        .filter(([, ftes]) => ftes > 0).length === 1
                                                                        ? 'justify-center min-w-max'
                                                                        : 'justify-start '
                                                                }`}
                                                            >
                                                                {(() => {
                                                                    const regionalFTEs = (activity.regionBreakdown ||
                                                                        activity.regionalFTEs ||
                                                                        activity.fteBreakdown ||
                                                                        activity.regions ||
                                                                        {}) as Record<string, number>

                                                                    const fteEntries = Object.entries(regionalFTEs).filter(([, ftes]) => ftes > 0)

                                                                    if (fteEntries.length === 0) {
                                                                        // Fallback: show total FTEs if no regional breakdown
                                                                        const totalFTEs = activity.ftesImpacted || activity.totalFTEs || activity.ftes || 0
                                                                        return totalFTEs > 0 ?
                                                                            <span
                                                                                className="region-badge">{totalFTEs} FTEs</span> :
                                                                            <span
                                                                                className="text-xs text-slate-400">-</span>
                                                                    }

                                                                    return fteEntries.map(([region, ftes]) => (
                                                                        <span
                                                                            key={region}
                                                                            className="region-badge"
                                                                        >
                                                  {region.replace('_', ' ')}: {ftes}
                                                </span>
                                                                    ))
                                                                })()}
                                                            </div>
                                                        </td>

                                                        {/* Column 3: Deliverable Automation % - CLEAN TEXT ONLY */}
                                                        <td className="text-right text-sm text-white whitespace-nowrap pr-3">
                                          <span className="text-teal-300 font-medium bg-transparent"
                                                style={{background: 'transparent !important'}}>
                                            {Math.round(activity.automationPercent || activity.automationPercentage || activity.automation || 0)}%
                                          </span>
                                                        </td>

                                                        {/* Column 4: Hours saved (deliverable) */}
                                                        <td className="text-right text-sm text-white whitespace-nowrap pr-3">
                                                            {(() => {
                                                                const fteCount = activity.ftesImpacted || activity.totalFTEs || activity.ftes || 0
                                                                // Use deliverable automation rate (not benchmark)
                                                                const deliverablePercent = activity.automationPercent || activity.automationPercentage || activity.automation || 0
                                                                const automationPercent = deliverablePercent / 100
                                                                const hoursPerYear = fteCount * TIME_CONFIG.HOURS_PER_FTE_YEAR
                                                                const hoursSaved = hoursPerYear * automationPercent
                                                                return `${Math.round(hoursSaved / 1000)}k`
                                                            })()}
                                                        </td>

                                                        {/* Column 5: Deliverable Savings ($) - CLEAN TEXT ONLY */}
                                                        <td className="text-right text-sm text-white whitespace-nowrap pr-3">
                                          <span className="bg-transparent"
                                                style={{background: 'transparent !important'}}>
                                            {(() => {
                                                const deliverablePercent = activity.automationPercent || activity.automationPercentage || activity.automation || 0;
                                                const deliverableSavings = calculateRegionalAutomationSavings(activity, deliverablePercent);
                                                return formatCurrency(deliverableSavings);
                                            })()}
                                          </span>
                                                        </td>

                                                        {/* Column 6: Expandable Details Chevron */}
                                                        <td className="text-center py-4 px-4">
                                                            <button
                                                                onClick={() => {
                                                                    const taskId = `${activity.activityName || activity.taskName || 'unnamed'}-${activityIndex}`;
                                                                    setExpandedTaskId(expandedTaskId === taskId ? null : taskId);
                                                                }}
                                                                className="p-1 hover:bg-slate-700 rounded transition-colors duration-200"
                                                                aria-expanded={expandedTaskId === `${activity.activityName || activity.taskName || 'unnamed'}-${activityIndex}`}
                                                                aria-label="View detailed metrics"
                                                            >
                                                                <ChevronDown
                                                                    className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
                                                                        expandedTaskId === `${activity.activityName || activity.taskName || 'unnamed'}-${activityIndex}`
                                                                            ? 'rotate-180'
                                                                            : ''
                                                                    }`}
                                                                />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {/* STEP 3: Enhanced Expandable Detail Row */}
                                                    {expandedTaskId === `${activity.activityName || activity.taskName || 'unnamed'}-${activityIndex}` && (
                                                        <tr>
                                                            <td colSpan={6}
                                                                className="bg-slate-800 rounded-md p-4 border border-slate-600 shadow-inner">
                                                                <div
                                                                    className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm text-white">
                                                                    <div>
                                                                        <span className="text-white">Benchmark automation:</span>
                                                                        <span className="text-teal-300 font-medium">
                                                    {(() => {
                                                        const deliverablePercent = activity.automationPercent || activity.automationPercentage || activity.automation || 0;
                                                        const benchmarkPercent = 0 // REMOVED: Hardcoded +30, must use database benchmarks;
                                                        return `${Math.round(benchmarkPercent)}%`;
                                                    })()}
                                                  </span>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <span
                                                                            className="text-white">Benchmark savings:</span>
                                                                        <span className="text-teal-300 font-medium">
                                                    {(() => {
                                                        const deliverablePercent = activity.automationPercent || activity.automationPercentage || activity.automation || 0;
                                                        const benchmarkPercent = 0 // REMOVED: Hardcoded +30, must use database benchmarks;
                                                        const benchmarkSavings = calculateRegionalAutomationSavings(activity, benchmarkPercent);
                                                        return formatCurrency(benchmarkSavings);
                                                    })()}
                                                  </span>
                                                                    </div>
                                                                    <div>
                                                                        <span
                                                                            className="text-white">Gap to benchmark:</span>
                                                                        <span className="text-teal-300 font-medium">
                                                    {(() => {
                                                        const deliverablePercent = activity.automationPercent || activity.automationPercentage || activity.automation || 0;
                                                        const deliverableSavings = calculateRegionalAutomationSavings(activity, deliverablePercent);
                                                        const benchmarkPercent = 0 // REMOVED: Hardcoded +30 must use database benchmarks;
                                                        const benchmarkSavings = calculateRegionalAutomationSavings(activity, benchmarkPercent);
                                                        const gap = benchmarkSavings - deliverableSavings;
                                                        return formatCurrency(gap);
                                                    })()}
                                                  </span>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <span className="text-white">Benchmark hours saved:</span>
                                                                        <span className="text-teal-300 font-medium">
                                                    {(() => {
                                                        const fteCount = activity.ftesImpacted || activity.totalFTEs || activity.ftes || 0;
                                                        // Use benchmark automation rate (database benchmarks only - no hardcoded values)
                                                        const deliverablePercent = activity.automationPercent || activity.automationPercentage || activity.automation || 0;
                                                        const benchmarkPercent = 0 // REMOVED: Hardcoded +30, must use database benchmarks;
                                                        const automationPercent = benchmarkPercent / 100;
                                                        const hoursPerYear = fteCount * TIME_CONFIG.HOURS_PER_FTE_YEAR;
                                                        const hoursSaved = hoursPerYear * automationPercent;
                                                        return `${Math.round(hoursSaved / 1000)}k hrs/year`;
                                                    })()}
                                                  </span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ));
                                        })()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
        {/* 
          ===========================================================================================
          EXPORT ANALYSIS SECTION
          ===========================================================================================
          
          PURPOSE:
          Provides comprehensive export capabilities for Value Assessment results.
          This section is conditionally rendered based on showExportOptions prop.
          
          VISIBILITY CONTROL:
          - Shown when: showExportOptions === true (default behavior)
          - Hidden when: showExportOptions === false (used for public/limited views)
          - Independent of pricing toggle (showFinancials) state
          
          EXPORT FORMATS:
          1. CSV - Complete data audit with all calculations
          2. HQ Export - High-quality images of all charts and tables
          3. PowerPoint - Professional presentation with embedded visuals
          4. Share - Generate public link for stakeholder access
          
          BUSINESS CONTEXT:
          Consultants use these exports to:
          - Share findings with C-suite executives
          - Create board presentations
          - Provide audit trails for financial validation
          - Enable stakeholder collaboration
        */}
        {showExportOptions && (
        <Card className="section-wrapper bg-gradient-to-br from-slate-800/60 to-slate-900/80 border-2 border-teal-500/30">
          <CardHeader className="pb-3 sm:pb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Share2 className="w-5 h-5 text-primary dark:text-primary" />
                <div>
                  <CardTitle className="text-lg sm:text-xl font-semibold text-foreground">Export Analysis</CardTitle>
                  <p className="text-xs sm:text-sm text-primary font-medium">Generate reports and share findings with stakeholders</p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">

              {/* Excel Export (Audit Template) - Updated from CSV */}
              <Button
                onClick={handleDownloadExcelTemplate}
                disabled={isExporting}
                variant="outline"
                className="export-excel-button flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600/50 h-10 sm:h-12 text-sm sm:text-base"
              >
                {isExporting ? (
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                ) : (
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                )}
                <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'Download Audit'}</span>
                <span className="sm:hidden">Excel</span>
              </Button>


              {/* Consolidated Savings Export */}
              <Button
                onClick={handleConsolidatedSheetExport}
                disabled={isConsolidatedExporting}
                variant="outline"
                className="flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600/50 h-10 sm:h-12 text-sm sm:text-base"
              >
                {isConsolidatedExporting ? (
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                ) : (
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                )}
                <span className="hidden sm:inline">{isConsolidatedExporting ? 'Exporting...' : 'Consolidated'}</span>
                <span className="sm:hidden">Savings</span>
              </Button>

              {/* PowerPoint Export */}
              <Button
                onClick={handlePowerPointDownload}
                disabled={exportCostBenefitLoading}
                variant="outline"
                className="export-powerpoint-button flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600/50 h-10 sm:h-12 text-sm sm:text-base"
              >
                {exportCostBenefitLoading ? (
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                ) : (
                  <FileImage className="w-3 h-3 sm:w-4 sm:h-4" />
                )}
                <span className="hidden sm:inline">{exportCostBenefitLoading ? 'Generating...' : 'PowerPoint'}</span>
                <span className="sm:hidden">PPT</span>
              </Button>

              {/* Share Summary */}
              <Button
                onClick={handleGenerateShareLink}
                disabled={isSharing}
                variant="outline"
                className="flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600/50 h-10 sm:h-12 text-sm sm:text-base"
              >
                <Share className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{isSharing ? 'Generating...' : 'Share Summary'}</span>
                <span className="sm:hidden">Share</span>
              </Button>

            </div>

          </CardContent>
        </Card>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md border-2 border-teal-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/80">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share className="w-5 h-5" />
                  Share Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Public Link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border border-border rounded-lg bg-muted text-sm"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(shareUrl)
                        toast({
                          title: "Link Copied",
                          description: "Share link copied to clipboard",
                          duration: 2000,
                        })
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  This link allows public access to view the cost savings summary without requiring login.
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowShareModal(false)}
                  >
                    Close
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => window.open(shareUrl, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );

}

export default memo(CostSavingsSummary)
